import gql from 'graphql-tag'
import _ from 'lodash'
import { Notify } from 'quasar'
import { APP_MODE_TYPES, appMode } from 'src/config'
import { _loadAndSetLocaleAsync, EN, languageFromLocale, REGION_AVAILABLE_LOCALES } from 'src/i18n'
import { PERMISSION_CATEGORIES, PERMISSIONS_BY_CATEGORY } from 'src/models/access-control/constants'
import { allMutations, CONSTRUCTOR_OPTION_KEYS, MAX_RETRY_COUNT, RETRY_DELAY_MS, TRANSLATABLE_SUB_KEY_TYPES } from 'src/models/model/constants'
import { ID, Int } from 'src/models/model/primitive-types'
import { timeContainer } from 'src/models/model/primitive-types/IsoDateTime'
import { configurationStatus } from 'src/models/model/reactive-state'
import { JsonObject, LocalizedJsonObject } from 'src/models/model/simple-types'
import { computeCollectionVariableName } from 'src/models/model/utils/collection-utils'
import { buildQuery, executeMutation } from 'src/models/model/utils/gql-utils'
import { getCurrentSessionIdentifier, isPrimitive, isSimpleType, nextLocalId } from 'src/models/model/utils/model-utils'
import { changes, mergeById, traverse, traverseProcess, withoutLocalIds } from 'src/models/model/utils/object-utils'
import { asId, downFirst, upFirst } from 'src/models/model/utils/string-utils'
import WeakRefSet from 'src/utils/WeakRefSet'
import { effectScope, getCurrentInstance, markRaw, nextTick, reactive, watch, watchEffect } from 'vue'

/**
 * @typedef {import('src/models/model/types').ModelOptions} ModelOptions
 * @typedef {import('src/models/model/types').ModelRawData} ModelRawData
 * @typedef {import('src/models/model/types').ModelRecordRawData} ModelRecordRawData
 * @typedef {import('src/models/model/types').ModelCollectionRawData} ModelCollectionRawData
 * @typedef {import('src/models/model/types').ModelRawDataAndOptions} ModelRawDataAndOptions
 * @typedef {import('src/models/model/types').GqlStructure} GqlStructure
 * @typedef {import('src/models/model/types').GqlQueryOptions} GqlQueryOptions
 * @typedef {import('src/models/model/types').GqlMutationOptions} GqlMutationOptions
 * @typedef {import('src/models/model/types').ModelFieldOptions} ModelFieldOptions
 */

watchEffect(() => {
  if (configurationStatus.notifiedClientConfigVersion === null) {
    configurationStatus.notifiedClientConfigVersion = configurationStatus.localClientConfigVersion
  }

  configurationStatus.unpushedChangeCount = configurationStatus.localClientConfigVersion - configurationStatus.notifiedClientConfigVersion
  configurationStatus.areUnpushedChanges = configurationStatus.localClientConfigVersion > configurationStatus.notifiedClientConfigVersion
})

/**
   * Model serves several purposes. It's a place to define data and behavior in an OOP manner. It's also used to specify
   * the graph structure of the data for querying and saving of the data.
   *
   * The model can either represent a single instance or a collection.
   *
   * The model and the collection are reactive and using in a Vue instance will automatically allow for reactivity. This is
   * true of fields and of methods that reference fields.
   *
   * Fields are either a relationship to another model, primitive (string, number), or a simple object (Array or Object).
   */
export default class Model {
  /**
   * The Vue app instance. Set by src/boot/apollo.js.
   * @type {import('vue').ComponentOptions | null}
   */
  static app = null

  /**
   * The Apollo client instance. Set by src/boot/apollo.js.
   * @type {import('@apollo/client/core').ApolloClient<import('@apollo/client/core').NormalizedCacheObject> | null}
   */
  static apollo = null

  /**
   * The ActionCable instance. Set by src/boot/apollo.js.
   * @type {import('actioncable').Cable | null}
   */
  static cable = null

  /**
   * The ConfigurationType instance. Set by src/models/index.js.
   * @type {import('src/models/ClientConfig').default | null}
   */
  static configuration = null

  /**
   * The ContextType instance. Currently ClientContext. Set by src/models/index.js.
   * @type {import('src/models/ClientContext').default | null}
   */
  static context = null

  /**
   * Whether to skip fetching the ClientContext. Set by src/models/index.js.
   * @type {boolean}
   */
  static skipContext = false

  /**
   * @type {WeakRefSet} - Set of all Model instances that have been fetched so far.
   */
  static allBaseQueries = new WeakRefSet()

  static async refetchAllBaseQueries (onLocaleFetch = null) {
    Model.context.clientReloadProgress = 0.6
    // Need to fetch configuration explicitly due to it be initially validated and not fetched,
    // so won't be included in allBaseQueries
    await Model.configuration.validate()
    Model.context.clientReloadProgress = 0.75
    // Set local and notified to match on site/circuit change
    configurationStatus.notifiedClientConfigVersion = configurationStatus.localClientConfigVersion

    let completed = 0
    const incrementProgress = () => {
      completed += 1
      const completedPercent = completed / queries.length
      Model.context.clientReloadProgress = Math.min(0.75 + (completedPercent * 0.25), 0.99)
    }

    const baseQueries = [...this.allBaseQueries.items()]
    const queries = _.map(baseQueries, (model) => {
      if (model.destroyed || model.silent) return Promise.resolve()

      return new Promise((resolve) => {
        // refetchAllBaseQueries may never complete if at least one of the model's ends up triggering a
        // duplicate `fetch()` call while this promise is still pending. The new fetch() call will unsubscribe
        // the watcher function that would have resolved this promise, leaving the promise forever unresolved.
        // For now, we can use a timeout to ensure the promise always gets resolved with a timeout.
        const timeoutSeconds = 4
        const timeoutPromise = new Promise((resolve) => setTimeout(resolve, timeoutSeconds * 1000))
        const modelFetchPromise = model.fetch(true)
        if (model.typeName === 'ClientContext') {
          // ClientContext holds the user's computed language so we must react to its language changes.
          modelFetchPromise.finally(async () => {
            if (model.currentLocale) {
              const locale = await _loadAndSetLocaleAsync(model.currentLocale)
              if (typeof onLocaleFetch === 'function') {
                onLocaleFetch(locale)
              }
            }
          })
        }
        Promise.race([modelFetchPromise, timeoutPromise]).finally(resolve)
      })
    })

    _.each(queries, (promise) => promise.finally(incrementProgress))

    await Promise.allSettled(queries)
    setTimeout(() => {
      Model.context.clientReloadProgress = 0
    }, 500)
  }

  /**
   * @type {ModelOptions|null}
   */
  originalOptions = null

  /**
   * @param {ModelRawData|ModelRawDataAndOptions} [data]
   * @param {ModelOptions} [options]
   */
  constructor (data = {}, options = {}) {
    // Uncomment for debugging where models are created
    // this.stackTrace = new Error().stack

    // Create a detached scope for all the model's effects so that they won't be
    // tracked by any Vue components
    this.modelScope = effectScope(true)
    // This object is WAY to complicated to track changes in. Mark this as raw and set up reactivity as we go.
    markRaw(this)

    if (!data) {
      /** @type {ModelRawData} */
      data = {}
    }
    // Supported named parameters
    if (data.options || data.data) {
      options = data.options || {}
      /** @type {ModelRawData} */
      data = data.data || options.isCollection ? [] : {}
    }

    this.originalOptions = options

    // Use the existence of structure as an indication the options have already been processed
    if (!options.structure) {
      options = this.processOptions(this.constructor, options)
    } else {
      options = { ...options }
    }
    this.modelScope.run(() => {
      this.options = reactive(options)
    })

    this.setupProxyCalls()
    this.setupReactiveProperties()
    this.setupComputedProperties(options.skipComputed)

    if (options.isCollection) {
      this.setData(data || [], true)
    } else {
      this.setData(_.extend({}, this.options.skipDefault ? {} : this.defaultData, data), true)
    }

    if (this.isTranslationEditingAllowed) {
      this.inferForcedLanguageFromLatestLanguage()
    }

    if (!this.options.skipWatchers) {
      this.setupWatchers()
    }

    if (this.options.autoSave) {
      this.setupAutoSave()
    }

    this.initialize && this.initialize()
  }

  get fields () {
    return this.constructor.fields
  }

  /**
   * Returns whether the model has a field configured with the given key.
   * @param {string} key - Property name
   */
  hasField (key) {
    return _.has(this.fields, key)
  }

  /**
   * Returns whether the field's current value was masked by the API.
   * @param {string} fieldName - The name of the field to check.
   */
  isFieldMaskedByApi (fieldName) {
    return this.maskedFields && _.includes(this.maskedFields, fieldName)
  }

  get fragments () {
    return this.constructor.fragments
  }

  get queries () {
    return this.constructor.queries
  }

  set queries (queries) {
    this.constructor.queries = queries
  }

  get displayName () {
    if (this.isLocal) {
      return ''
    } else {
      return this.data?.description || this.data?.name || (this.typeName + ' ' + this.data?.id)
    }
  }

  get mutations () {
    if (this.constructor.mutations) return this.constructor.mutations

    this.constructor.mutations = {}
    return this.constructor.mutations
  }

  set mutations (mutations) {
    this.constructor.mutations = mutations
  }

  static level = undefined
  get level () {
    return this.constructor.level
  }

  static paginate = false
  get paginate () {
    return this.constructor.paginate
  }

  static deletable = false
  get deletable () {
    return this.constructor.deletable
  }

  static hasAuditSummary = false
  get hasAuditSummary () {
    return this.constructor.hasAuditSummary
  }

  static duplicatable = false
  get duplicatable () {
    return this.constructor.duplicatable
  }

  static versioned = false
  get versioned () {
    return this.constructor.versioned
  }

  static atlasable = false
  get atlasable () {
    return this.constructor.atlasable
  }

  static isExportable = false
  get isExportable () {
    return this.constructor.isExportable
  }

  static validatable = false
  get validatable () {
    return this.constructor.validatable
  }

  static noId = false
  get noId () {
    return this.constructor.noId
  }

  static testEnvAppMode = null

  get testEnvAppMode () {
    return this.constructor.testEnvAppMode
  }

  set testEnvAppMode (testEnvAppMode) {
    this.constructor.testEnvAppMode = testEnvAppMode
  }

  static get isTranslationEditingAllowed () {
    return (this.testEnvAppMode || appMode) === APP_MODE_TYPES.ADMIN
  }

  get isTranslationEditingAllowed () {
    return this.constructor.isTranslationEditingAllowed
  }

  static isTranslatable = false
  get isTranslatable () {
    return this.constructor.isTranslatable
  }

  // When merging changes there may be some fields we want to exclude from the merge, these are defined here
  static mergeFieldsToOmit = []
  get mergeFieldsToOmit () {
    return this.constructor.mergeFieldsToOmit
  }

  /**
   * Populates the model class's fields, fragments, queries, mutations, computed, and watchers.
   * To be implemented by each Model subclass.
   */
  static define () {}

  /**
   * Merges defined properties from a concern into the model class. This
   * occurs after the target model's `define` method has been called.
   *
   * Adds additional fields (e.g. archivedAt IsoDateTime)
   * Adds additional static properties (e.g. archivable, versioned)
   */
  static includeConcerns () {
    for (const concern of this.concerns) {
      // Merge new fields from the concern, prioritizing local class fields
      if (!_.isEmpty(concern.fields)) {
        // Use _.defaults to ensure any fields already defined on this are not overridden by the concern
        _.defaults(this.fields, concern.fields)
      }

      if (!_.isEmpty(concern.computed)) {
        // Use _.defaults to ensure any computed already defined on this are not overridden by the concern
        _.defaults(this.computed, concern.computed)
      }

      if (!_.isEmpty(concern.watchers)) {
        // Use _.defaults to ensure any watchers already defined on this are not overridden by the concern
        _.defaults(this.watchers, concern.watchers)
      }

      if (!_.isEmpty(concern.queries)) {
        // Use _.defaults to ensure any queries already defined on this are not overridden by the concern
        _.defaults(this.queries, concern.queries)
      }

      if (!_.isEmpty(concern.mutations)) {
        // Use _.defaults to ensure any mutations already defined on this are not overridden by the concern
        _.defaults(this.mutations, concern.mutations)
      }

      // Include any static properties (fields, methods) defined by the concern
      for (const property of concern.staticProperties) {
        // Skip any properties that are already defined on the class
        if (property in this) continue

        if (_.isNil(concern[property]) || typeof concern[property] !== 'object') {
          // Directly copies primitives, classes, and functions (by value)
          this[property] = concern[property]
        } else {
          // Deep clones objects and arrays (by value), to ensure any changes to the property don't cross Models
          this[property] = _.cloneDeep(concern[property])
        }
      }

      // Include any instance methods defined by the concern
      // Excludes instance fields, which are only defined if we instantiate the Concern
      for (const method of concern.instanceMethods) {
        // Skip any methods that are already defined on the class
        if (method in this.prototype) continue

        this.prototype[method] = concern.prototype[method]
      }

      // Call concern.define to perform complex setup
      concern.define.call(this)
    }
  }

  // Add default fragment, queries, and mutations
  static staticDefined = false
  static staticInitialization () {
    if (this.staticDefined) return
    this.staticDefined = true

    /** @type {Record<string, ModelFieldOptions>} */
    this.fields = {}
    /** @type {Record<string, string[]>} */
    this.fragments = {}
    /** @type {Record<string, GqlQueryOptions>} */
    this.queries = {}
    /** @type {Record<string, GqlMutationOptions>} */
    this.mutations = {}
    /** @type {Record<string, (...args: any[]) => any>} */
    this.computed = {}
    this.watchers = {}
    /** @type {Array<typeof import('./concerns/Concern').default>} */
    this.concerns = []

    // All user definitions defined here
    this.define()
    this.includeConcerns()

    // Add variables to determine is model has already been saved when mutation is called
    // If we don't get a response from the API but the model does save, we don't want to save the same model again as new data
    if (this.name !== 'ClientContext' && this.name !== 'ClientConfig') {
      _.extend(this.fields, {
        localId: {
          type: ID,
          default: null,
          input: true
        },
        sessionIdentifier: {
          type: ID,
          default: null,
          input: true
        }
      })
    }

    this.isTranslatable = _.some(this.fields, (field) => field.translatable || field.type === LocalizedJsonObject)
    if (this.isTranslationEditingAllowed) {
      // It's necessary for non-translatable models to also fetch their `_lang` values so they
      // can propagate down the language value to translatable sub-models.
      _.extend(this.fields, {
        _lang: {
          type: String,
          default: EN,
          output: true
        }
      })
      if (this.isTranslatable) {
        _.extend(this.fields, {
          englishFallbackValues: {
            type: JsonObject,
            default: null,
            output: true,
            includeInQueryIf: (resultsQueryOptions) => {
              return resultsQueryOptions.forceLanguage && resultsQueryOptions.forceLanguage !== EN
            }
          }
        })
      }
    }

    this.computedDefinitions = {}
    _.each(this.staticComputed, (computeFunction, name) => {
      if (this.computedDefinitions[name]) throw new Error('Naming collision for static computed ' + name)

      this.computedDefinitions[name] = {
        type: 'static',
        getter: computeFunction
      }
    })
    _.each(this.computed, (computeFunction, name) => {
      if (this.computedDefinitions[name]) throw new Error('Naming collision for computed ' + name)

      this.computedDefinitions[name] = {
        type: 'computed',
        getter: computeFunction
      }
    })
    _.each(this.fields, (fieldOptions, name) => {
      if (!fieldOptions.computed) return

      if (this.computedDefinitions[name]) throw new Error('Naming collision for computed field ' + name)

      if (!fieldOptions.setter) {
        fieldOptions.setter = async function (value) {
          const oldValue = this.data[name]
          this.data[name] = value
          if (oldValue && oldValue !== value && oldValue.destroy && oldValue.hasAncestor(this)) {
            await nextTick()
            oldValue.destroy()
          }
        }
      }

      this.computedDefinitions[name] = {
        type: 'field',
        getter: fieldOptions.computed,
        setter: fieldOptions.setter
      }
    })

    this.configurationFields = _.pickBy(this.fields, field => field.reference && (_.isArray(field.type) ? field.type[0].isConfiguration : field.type.isConfiguration))

    this.configurationIdFields = {}
    _.each(this.configurationFields, (configurationField, name) => {
      const { isCollection, Type } = this.fieldType(name)

      let type
      let defaultFunction
      if (isCollection) {
        type = [ID]
        defaultFunction = () => []
      } else {
        type = ID
        defaultFunction = null
      }

      const definition = {
        type,
        default: defaultFunction,
        excludeFromDefault: configurationField.excludeFromDefault
      }

      if (configurationField.output || configurationField.acceptsNestedAttributes) {
        definition.output = true // Output only because input is all attributes
      }

      if (configurationField.local) {
        definition.local = true
      }

      const idFieldName = asId(name, isCollection)
      this.configurationIdFields[idFieldName] = definition

      // Define the compute method
      if (this.computedDefinitions[name]) console.log('WARN: Naming collision for computed reference field ' + name)

      this.computedDefinitions[name] = {
        type: 'reference',
        getter () {
          if (!this.data) return

          if (!Model.configuration) {
            console.log('No configuration attached to Model: ' + name)
            return this.buildDefaultField(name)
          }

          const configurationModels = Model.configuration[Type.configurationCollectionVariableName]
          if (!configurationModels) {
            console.log("Couldn't find models of this type in the configuration " + Type.configurationCollectionVariableName + ' for ' + name)

            return this.buildDefaultField(name)
          }

          if (isCollection) {
            // Use ID-indexed lookup map for efficient O(1) lookups instead of O(n) filtering
            return { data: _.map(this.data[idFieldName], id => configurationModels.dataById?.[id]).filter(Boolean) }
          } else {
            // Use ID-indexed lookup map for efficient O(1) lookup instead of O(n) find
            const result = configurationModels.dataById?.[this.data[idFieldName]]

            if (result) return result

            return this.buildDefaultField(name)
          }
        },
        setter (data) {
          if (isCollection) {
            this.data[idFieldName] = data ? _.map(data.data, 'id') : []
          } else {
            this.data[idFieldName] = data?.id || null
          }
        }
      }
    })
    _.extend(this.fields, this.configurationIdFields)

    // Add default fragment based of all primitive types
    if (!this.fragments.default) {
      this.fragments.default = _(this.fields).pickBy(field => ((isPrimitive(field.type) || isSimpleType(field.type)) && !field.excludeFromDefault) || field.local).keys().value()
    }

    // Add all fragments
    if (!this.fragments.all) {
      this.fragments.all = _.keys(this.fields)
    }

    // Add fragment for just the ids
    if (!this.fragments.id && _(this.fields).keys().includes('id')) {
      this.fragments.id = ['id']
    }

    // Add fragment for just translatable fields, if any
    if (!this.fragments.translations) {
      const translatableFieldNames = _(this.fields).pickBy('translatable').keys().value()
      if (translatableFieldNames.length > 0) {
        this.fragments.translations = translatableFieldNames
      }
    }

    // Default queries for single model and collection
    if (!this.queries[this.variableName]) {
      this.queries[this.variableName] = {
        arguments: {
          id: {
            type: ID,
            required: true
          },
          siteId: {
            type: ID,
            required: false
          }
        },
        results: {
          [this.variableName]: this
        }
      }

      if (this.atlasable) {
        this.queries[this.variableName].arguments.atlas = {
          type: Boolean,
          required: false
        }
      }
    }
    if (!this.queries[this.collectionVariableName]) {
      if (this.paginate) {
        this.queries[this.collectionVariableName] = {
          arguments: {
            limit: {
              type: Int,
              required: false
            },
            hasDisplayOrder: {
              type: Boolean,
              required: false
            },
            displayOrderWeek: {
              type: String,
              required: false
            },
            currentMovieId: {
              type: ID,
              required: false
            },
            movieIdsToExclude: {
              type: [ID],
              required: false
            },
            offset: {
              type: Int,
              required: false
            },
            noCount: {
              type: Boolean,
              default: false
            },
            isBulkAdjustment: {
              type: Boolean,
              default: false
            },
            bulkAdjustmentType: {
              type: String,
              required: false
            },
            transferLocationId: {
              type: ID,
              required: false
            },
            transferRegisterStationId: {
              type: ID,
              required: false
            },
            effectiveAt: {
              type: String,
              required: false
            },
            force: {
              type: Boolean,
              required: false
            },
            locationId: {
              type: ID,
              required: false
            },

            archived: {
              type: Boolean,
              required: false
            },

            orderBy: {
              type: String,
              required: false
            },
            descending: {
              type: Boolean,
              required: false
            },

            searchString: {
              type: String,
              required: false
            },
            // TODO, build dynamically from configuration specification
            userId: {
              type: ID,
              required: false
            },
            ids: {
              type: [ID],
              required: false
            },
            siteId: {
              type: ID,
              required: false
            },
            siteIds: {
              type: [ID],
              required: false
            },
            movieId: {
              type: ID,
              required: false
            },
            movieIds: {
              type: [ID],
              required: false
            },
            weeksOf: {
              type: [String],
              required: false
            },
            screenId: {
              type: ID,
              required: false
            },
            boxOfficeReportId: {
              type: ID,
              required: false
            },
            studioId: {
              type: ID,
              required: false
            },
            studioIds: {
              type: [ID],
              required: false
            },
            employeeId: {
              type: ID,
              required: false
            },
            incomingEmployeeId: {
              type: ID,
              required: false
            },
            tabTransferType: {
              type: String,
              required: false
            },
            hasDeliveryLocation: {
              type: Boolean,
              required: false
            },
            forShowings: {
              type: String,
              required: false
            },
            showAllSites: {
              type: Boolean,
              required: false
            },
            salesChannel: {
              type: String,
              required: false
            },
            orderByIds: {
              type: [ID],
              required: false
            },
            userRole: {
              type: String,
              required: false
            },
            collectedUserId: {
              type: ID,
              required: false
            },
            registerStationId: {
              type: ID,
              required: false
            },
            paymentConnectorConfigurationId: {
              type: ID,
              required: false
            },
            developerMode: {
              type: Boolean,
              required: false
            },
            filter: {
              type: String,
              required: false
            },
            orderId: {
              type: ID,
              required: false
            },
            itemClassId: {
              type: ID,
              required: false
            },
            isMarathon: {
              type: Boolean,
              required: false
            },
            titleClassId: {
              type: ID,
              required: false
            },
            titleClassIds: {
              type: [ID],
              required: false
            },
            membershipTypeIds: {
              type: [ID],
              required: false
            },
            anyShowingBadgeIds: {
              type: [ID],
              required: false
            },
            everyShowingBadgeIds: {
              type: [ID],
              required: false
            },
            transferId: {
              type: ID,
              required: false
            },
            voidTransferId: {
              type: ID,
              required: false
            },
            disputed: {
              type: Boolean,
              required: false
            },
            recomputeDisputes: {
              type: Boolean,
              required: false
            },
            recomputeTransfers: {
              type: Boolean,
              required: false
            },
            recomputeAccountingDay: {
              type: Boolean,
              required: false
            },
            showingId: {
              type: ID,
              required: false
            },
            giftCardId: {
              type: ID,
              required: false
            },
            voucherId: {
              type: ID,
              required: false
            },
            tableName: {
              type: String,
              required: false
            },
            reportId: {
              type: ID,
              required: false
            },
            reportCategoryId: {
              type: ID,
              required: false
            },
            jobLogTypeId: {
              type: ID,
              required: false
            },
            jobLogSubTypeId: {
              type: ID,
              required: false
            },
            state: {
              type: String,
              required: false
            },
            states: {
              type: [String],
              required: false
            },
            onlyUserVouchers: {
              type: Boolean,
              required: false
            },
            onlyCardVouchers: {
              type: Boolean,
              required: false
            },
            onlyVoucherPackages: {
              type: Boolean,
              required: false
            },
            voucherTypeId: {
              type: ID,
              required: false
            },
            rangeStart: {
              type: String,
              required: false
            },
            orderToExclude: {
              type: ID,
              required: false
            },
            startedViaQrCode: {
              type: Boolean,
              required: false
            },
            noEmployee: {
              type: Boolean,
              required: false
            },
            rangeEnd: {
              type: String,
              required: false
            },
            showingsToday: {
              type: Boolean,
              required: false
            },
            type: {
              type: String,
              required: false
            },
            subtype: {
              type: String,
              required: false
            },
            types: {
              type: [String],
              required: false
            },
            period: {
              type: String,
              required: false
            },
            published: {
              type: Boolean,
              required: false
            },
            appMode: {
              type: String,
              required: false
            },
            currentVersion: {
              type: String,
              required: false
            },
            versionAvailableOnFrontendServer: {
              type: String,
              required: false
            },
            inventoryItemId: {
              type: ID,
              required: false
            },
            inventorySupplierId: {
              type: ID,
              required: false
            },
            adjustmentType: {
              type: String,
              required: false
            },
            uncommitted: {
              type: Boolean,
              required: false
            },
            cinemaDataMigrationId: {
              type: ID,
              required: false
            },
            deliveryLocation: {
              type: String,
              required: false
            },
            date: {
              type: String,
              required: false
            },
            printTemplateId: {
              type: ID,
              required: false
            },
            orderItemIds: {
              type: [ID],
              required: false
            },
            splitByPosition: {
              type: Boolean,
              required: false
            },
            ticketNumber: {
              type: Number,
              required: false
            },
            activityId: {
              type: ID,
              required: false
            },
            activitySessionId: {
              type: ID,
              required: false
            },
            balanceState: {
              type: String,
              required: false
            },
            hasBalance: {
              type: Boolean,
              required: false
            },
            autoAssignable: {
              type: Boolean,
              required: false
            },
            employeeJobTypeId: {
              type: ID,
              required: false
            },
            dataMigrationId: {
              type: ID,
              required: false
            },
            studioSettlementId: {
              type: ID,
              required: false
            },
            movieTermId: {
              type: ID,
              required: false
            },
            movieTermIds: {
              type: [ID],
              required: false
            },
            batched: {
              type: String,
              required: false
            },
            customerioSegmentTaskId: {
              type: ID,
              required: false
            },
            connected: {
              type: Boolean,
              required: false
            },
            modelClass: {
              type: String,
              required: false
            },
            modelId: {
              type: ID,
              required: false
            },
            orderState: {
              type: String,
              required: false
            },
            tipShareId: {
              type: ID,
              required: false
            }
          },
          results: {
            [this.collectionVariableName]: [this]
          }
        }

        this.queries[`${this.variableName}Library`] = {
          arguments: {
            limit: {
              type: Int,
              required: false
            },
            offset: {
              type: Int,
              required: false
            },

            orderBy: {
              type: String,
              required: false
            },
            descending: {
              type: Boolean,
              required: false
            },

            searchString: {
              type: String,
              required: false
            },
            userId: {
              type: ID,
              required: false
            },
            state: {
              type: String,
              required: false
            }
          },
          results: {
            [this.collectionVariableName]: [this]
          }
        }

        if (this.atlasable) {
          this.queries[`${this.variableName}Atlas`] = {
            arguments: {
              limit: {
                type: Int,
                required: false
              },
              offset: {
                type: Int,
                required: false
              },

              orderBy: {
                type: String,
                required: false
              },
              descending: {
                type: Boolean,
                required: false
              },

              searchString: {
                type: String,
                required: false
              },
              userId: {
                type: ID,
                required: false
              },
              state: {
                type: String,
                required: false
              },
              atlas: {
                type: Boolean,
                required: false
              }
            },
            results: {
              [this.collectionVariableName]: [this]
            }
          }
        }
      } else {
        this.queries[this.collectionVariableName] = {
          results: {
            [this.collectionVariableName]: [this]
          }
        }
        this.queries[`${this.variableName}Library`] = {
          results: {
            [this.collectionVariableName]: [this]
          }
        }
      }
    }

    if (!this.mutations[`addOrUpdate${this.name}`]) {
      this.mutations[`addOrUpdate${this.name}`] = {
        arguments: {
          [`${this.variableName}Input`]: {
            type: this,
            required: true
          }
        },
        results: {
          [this.variableName]: [this],
          errors: [GqlError]
        }
      }
    }

    if (!this.mutations[`delete${this.name}`]) {
      this.mutations[`delete${this.name}`] = {
        arguments: {
          id: {
            type: ID,
            required: true
          }
        },
        results: {
          [this.variableName]: this,
          errors: [GqlError]
        }
      }

      if (this.atlasable) {
        this.mutations[`delete${this.name}`].arguments.atlas = {
          type: Boolean,
          required: true,
          default: false
        }
      }
    }

    if (!this.mutations[`duplicate${this.collectionTypeName}`]) {
      this.mutations[`duplicate${this.collectionTypeName}`] =
      {
        arguments: {
          ids: {
            type: [ID],
            required: true
          }
        },
        results: {
          [this.collectionVariableName]: this,
          errors: [GqlError]
        }
      }
      if (this.atlasable) {
        this.mutations[`duplicate${this.collectionTypeName}`].arguments.atlas = {
          type: Boolean,
          required: true,
          default: false
        }
      }
    }

    if (!allMutations.archiveArchivableModel) {
      allMutations.archiveArchivableModel = {
        arguments: {
          modelClass: {
            type: String,
            required: true
          },
          modelId: {
            type: ID,
            required: true
          },
          nullify: {
            type: Boolean,
            required: false
          }
        },
        results: {
          success: Boolean,
          errors: [GqlError]
        }
      }
    }

    if (!allMutations.unarchiveArchivableModel) {
      allMutations.unarchiveArchivableModel = {
        arguments: {
          modelClass: {
            type: String,
            required: true
          },
          modelId: {
            type: ID,
            required: true
          }
        },
        results: {
          success: Boolean,
          errors: [GqlError]
        }
      }
    }

    if (!this.mutations[`duplicate${this.name}ToSite`]) {
      this.mutations[`duplicate${this.name}ToSite`] = {
        arguments: {
          libraryModelId: {
            type: ID,
            required: true
          },
          siteId: {
            type: ID,
            required: true
          },
          force: {
            type: Boolean,
            required: false,
            default: false
          }
        },
        results: {
          [this.variableName]: this,
          errors: [GqlError]
        }
      }
    }
  }

  setupProxyCalls () {
    // Set up a proxy call for each field so the field can be called directly on the model
    _.each(_.keys(this.fields), (key) => {
      if (Object.hasOwnProperty.call(this, key)) {
        console.log('Naming collision, the field specified is already used on the model, will not proxy. Access through data as: model.data.' + key)
        // TODO, can delete later with this warning in place
        throw new Error('Naming collision, the field specified is already used on the model: ' + key)
      }

      const { field, isConfigurationField, isConfigurationIdField, isCollection } = this.fieldType(key)

      // Generic proxy will use underlying reactivity of the data object if present
      if (!this.options.noReference && isConfigurationField && !(field.acceptsNestedAttributes && this.options.editing) && !field.computed) {
        this.computedFieldNames.push(key)
      } else if (isConfigurationIdField && isCollection) {
        Object.defineProperty(this, key, {
          get: () => this.data ? this.data[key] : null,
          set: (value) => { this.data[key] = value ? _.sortBy(value) : value },
          enumerable: true,
          configurable: false
        })
      } else if (this.fields[key].customGetter) {
        Object.defineProperty(this, key, {
          get: () => this[this.fields[key].customGetter],
          set: async function (value) {
            const oldValue = this.data[key]
            this.data[key] = value
            if (oldValue && oldValue !== value && oldValue.destroy && oldValue.hasAncestor(this)) {
              await nextTick()
              oldValue.destroy()
            }
          },
          enumerable: true,
          configurable: false
        })
      } else if (this.fields[key].computed) {
        this.computedFieldNames.push(key)
      } else {
        Object.defineProperty(this, key, {
          get: () => this.data ? this.data[key] : null,
          set: async function (value) {
            const oldValue = this.data[key]
            this.data[key] = value
            if (oldValue && oldValue !== value && oldValue.destroy && oldValue.hasAncestor(this)) {
              await nextTick()
              oldValue.destroy()
            }
          },
          enumerable: true,
          configurable: false
        })
      }
    })
  }

  setupReactiveProperties () {
    this.directAccessOfReactiveProperties = {
      loading: false,
      settingData: false,
      loaded: false,
      data: null,
      count: null,
      originalData: null,
      editing: false,
      watcherRecomputeId: 0,
      preventDestroyIds: [],
      language: EN,
      previewing: false
    }

    this.reactiveProperties = this.createReactive(this.directAccessOfReactiveProperties, this.options.nonReactive)

    // Set up a proxy call for each reactive property directly on the model
    _.each(_.keys(this.reactiveProperties), (key) => {
      if (Object.hasOwnProperty.call(this, key)) {
        throw new Error('Naming collision, the reactive property is already defined on the model: ' + key)
      }

      Object.defineProperty(this, key, {
        get: () => this.reactiveProperties[key],
        set: (value) => {
          const shouldMarkRaw = value !== null && value !== undefined
          this.reactiveProperties[key] = shouldMarkRaw ? markRaw(value) : value
        },
        enumerable: true,
        configurable: false
      })
    })
  }

  static staticComputed = {
  }

  get isDirty () {
    return !_.isEqual(this.deepCloneData(true, true), this.originalDataForTrackingChanges)
  }

  get describeDirty () {
    const currentData = this.deepCloneData(true, true)
    return changes(currentData, this.originalDataForTrackingChanges)
  }

  get isLocal () {
    return this.id < 0
  }

  get isRemote () {
    return this.id > 0
  }

  get missingDataPolicy () {
    // return this.options.missingDataPolicy || this.parent?.missingDataPolicy || 'ignore'
    return 'ignore'
  }

  get extraDataPolicy () {
    // return this.options.extraDataPolicy || this.parent?.extraDataPolicy || 'infer'
    return 'infer'
  }

  get sortOrder () {
    return this.options.sortOrder || (this.fields.displayOrder && !this.constructor.ignoreDisplayOrder ? 'display-order' : 'natural')
  }

  computedFieldNames = []
  setupComputedProperties () {
    const computedFieldNames = _.uniq([..._.keys(this.constructor.staticComputed), ..._.keys(this.constructor.computed), ...this.computedFieldNames])
    this.allComputedFieldNames = computedFieldNames

    const computedData = {}
    _.each(computedFieldNames, key => { computedData[key] = null })

    this.computedData = this.createReactive(computedData, false)

    _.each(computedFieldNames, name => {
      if (Object.hasOwnProperty.call(this, name)) {
        throw new Error('Naming collision, the computed property is already defined on the model: ' + name)
      }

      const definition = this.constructor.computedDefinitions[name]
      if (!definition) throw new Error('Expected to find definition of computed property named ' + name)

      // Set up a proxy call for each computed property directly on the model
      Object.defineProperty(this, name, {
        get: () => {
          // Only configure the computed property when it's called
          if (!this.destroyed && (!this.computedWatchers[name] || (this.computedWatchers[name].scope && !this.computedWatchers[name].scope.active))) {
            if (getCurrentInstance()) {
              // If we have a reference to a vuemodel, we should create the computed property outside of the execution context (setTimeout)
              // By doing this outside of the execution context we can avoid any reference to the vuemodel in the computed property which prevents dependency tracking in the watcher
              // If we don't do this the vue model will be added as a dependency to the watcher and will cause a memory leak
              if (!this.computedData[name]) {
                this.computedData[name] = definition.getter.call(this)
                setTimeout(() => {
                  this.createComputed(name, definition.getter)
                }, 0)
              }
            } else {
              this.createComputed(name, definition.getter)
            }
          } else if (this.destroyed) {
            // If the object is destroyed, still evaluate and get the value, but not reactively. This will allow values to still be returned from destroyed object in the event it happens, but also prevents a memory leak.
            // console.log('WARNING: Getter called for: ' + this.typeName + ', id:' + this.id + ', field: ' + name)
            return definition.getter.call(this)
          }

          return this.computedData[name]
        },
        set: (value) => {
          if (!definition.setter) throw new Error('Cannot set a computed property:' + name)

          definition.setter.call(this, value)
        },
        enumerable: false,
        configurable: false
      })
    })
  }

  resetComputedFields () {
    _.each(this.computedWatchers, watcher => watcher.stopper())
    this.computedWatchers = {}
    this.initializedComputedProperties = new Set()
  }

  setupWatchers () {
    // Set up watchers and store the function for stopping
    _.each(this.constructor.watchers, (args) => {
      const source = args[0]
      const callback = args[1]
      const options = args[2]

      this.createWatcher(source.bind(this), callback.bind(this), options, source.toString())
    })
  }

  setupAutoSave () {
    const saveOnDirty = () => {
      if (!this.isCollection && this.isDirty && !this.editing && this.isRemote) {
        this.save()
      }
    }
    this.createWatcher(() => [this.isDirty, this.editing], _.debounce(saveOnDirty, 500))
  }

  get length () {
    return this.data?.length || 0
  }

  get isInCollection () {
    return !!(this.parent &&
      this.parent.isCollection &&
      _.some(this.parent.data, model => model?.id === this.id))
  }

  get isSubmodel () {
    return !this.options.baseQuery
  }

  get fieldsChanged () {
    const originalData = this.originalDataForTrackingChanges
    const currentData = this.deepCloneData(true, true)
    return _.compact(_.map(this.allSpecifiedFieldNames, field => {
      const { Type } = this.fieldType(field)
      const primitive = isPrimitive(Type)
      if (!_.isEqual(currentData[field], originalData[field])) { return { field, old: primitive ? originalData[field] : null, new: primitive ? currentData[field] : null } }
    }))
  }

  get fieldsChangedHtmlMessage () {
    return _.map(this.fieldsChanged, changed => {
      if (changed.old === null && changed.new === null) return '<div class="text-bold">' + _.startCase(changed.field) + ' Changed' + '</div>'
      return '<div><span class="text-bold">' + _.startCase(changed.field) + ':</span> ' + '<span class="text-red text-strike">' + changed.old + '</span> <span class="text-green text-bold">' + changed.new + '</span></div>'
    })
  }

  /**
   * @param {Model} Type
   * @param {ModelOptions} options
   * @returns {ModelOptions}
   */
  processOptions (Type, options) {
    if (!options) {
      options = {}
    }
    if (_.isArray(options)) {
      options = options[0]
    }

    // Options can be a string representing the name of a fragment of fields
    if (_.isString(options)) {
      options = { fields: options }
    }

    // A shorthand for 'default' is true
    if (options === true) {
      options = { fields: 'default' }
    }

    // Ensure we don't edit the passed in options
    options = { ...options }

    // Check for unexpected options
    // TODO: this unknown options check is broken, it just results in NaN. The fix is to use _.difference() instead
    const unknownOptions = _.keys(options) - _.keys(Type.fields) - CONSTRUCTOR_OPTION_KEYS
    if (unknownOptions.length) {
      throw new Error(`Unknown options or fields for [${Type.typeName}] (${unknownOptions.join(', ')})`)
    }

    let fields
    if (!options.fields) {
      fields = Type.fragments?.default
      if (!fields) {
        // throw new Error(`Unable to find fragment of name [default] on [${Type.name}]`)
        console.log(`Unable to find fragment of name [default] on [${Type.name}]`)
        fields = []
      }
    } else if (_.isString(options.fields)) {
      if (!Type.fragments) {
        throw new Error(`No fragments found on [${Type.name}], make sure model has been registered`)
      }
      fields = Type.fragments[options.fields]
      if (!fields) {
        throw new Error(`Unable to find fragment of name [${options.fields}] on [${Type.name}]`)
      }
    } else if (Array.isArray(options.fields)) {
      fields = options.fields
      _.each(fields, (field) => {
        if (!Type.fields[field]) {
          throw new Error(`Field definition for [${field}] not found on [${Type.name}]`)
        }
      })
    } else {
      throw new Error('Unhandled condition')
    }

    if (Type.isTranslationEditingAllowed) {
      if (!_.includes(fields, '_lang')) {
        fields.push('_lang')
      }
      if (Type.isTranslatable && !_.includes(fields, 'englishFallbackValues')) {
        fields.push('englishFallbackValues')
      }
    }

    if (Type.hasMaskableFields && !_.includes(fields, 'maskedFields')) {
      fields.push('maskedFields')
    }

    options.fields = fields

    // Recursively build the structure options that includes all fields and all subModels specified
    if (!options.structure) {
      options.structure = {}

      // Get all fields including ones where the options were provided
      const allSpecifiedFieldNames = options.fields.concat(_(options).keys().filter((name) => Type.fields[name]).value())

      const ParentType = Type
      _.each(allSpecifiedFieldNames, (fieldName) => {
        const { Type, isCollection, isConfigurationField, field } = ParentType.fieldType(fieldName)

        if (isConfigurationField && !(field.acceptsNestedAttributes && options.editing)) {
          // Convert reference field to reference id field
          options.structure[asId(fieldName, isCollection)] = true
        } else if (isPrimitive(Type) || isSimpleType(Type)) {
          options.structure[fieldName] = typeof field.includeInQueryIf === 'function' ? field.includeInQueryIf : true
        } else if (field.skipCreate) {
          return null
        } else {
          const structureOptions = this.processOptions(Type, options[fieldName]).structure
          if (isCollection) {
            options.structure[fieldName] = [structureOptions]
          } else {
            options.structure[fieldName] = structureOptions
          }
        }
      })
    }

    return options
  }

  get defaultData () {
    const defaultData = this.deepCloneObjectData(this.buildDefault(), false, false, [])
    return defaultData
  }

  // Recursively build default as Model objects
  buildDefault () {
    const output = {}

    if (_.isArray(this.options.structure)) {
      throw new Error('DID NOT EXPECT ARRAY HERE')
    }

    // Get defaults for all submodels. If the submodel is specified in the structure, use that, otherwise use the default defined on the Model
    _.each(_.keys(this.options.structure), fieldName => {
      output[fieldName] = this.buildDefaultField(fieldName)
    })

    return output
  }

  builtDefaultFields = {}
  buildDefaultField (fieldName) {
    let options = this.options.structure[fieldName]

    if (_.isArray(options)) {
      options = options[0]
    }

    const field = this.fields[fieldName]
    if (!field) {
      throw new Error(`Could not find submodel field [${fieldName}] on [${this.typeName}]`)
    }

    const { Type, isCollection } = this.fieldType(fieldName)

    if ((isPrimitive(Type) || isSimpleType(Type)) && !isCollection) {
      return _.isFunction(field.default) ? field.default() : field.default
    } else {
      const config = {}
      config.isCollection = isCollection
      if (_.isObject(options)) {
        config.structure = options
      }

      let defaultData
      if (isCollection) {
        defaultData = []
      } else {
        defaultData = {}
      }

      if (field.default === null) {
        defaultData = null
      } else if (field.default) {
        if (!_.isFunction(field.default)) {
          throw new Error(`Default value for field [${fieldName}] on [${this.typeName}] must be a function`)
        }
        defaultData = field.default()

        if (isCollection && !_.isArray(defaultData)) {
          defaultData = [defaultData]
        }
      }

      if (isSimpleType(Type) || isPrimitive(Type)) {
        return defaultData
      } else if (defaultData === null) {
        return null
      } else {
        // TODO Can we actually skip these or do we need them???
        config.skipComputed = true
        config.skipWatchers = true
        if (this.options.nonReactive) {
          config.nonReactive = true
        }

        if (!this.builtDefaultFields[fieldName]) {
          this.builtDefaultFields[fieldName] = new Type(defaultData?.data || defaultData, config)
        }

        return this.builtDefaultFields[fieldName]
      }
    }
  }

  get allSpecifiedFieldNames () {
    return _.keys(this.options.structure)
  }

  get pessimisticData () {
    return this.originalData
  }

  get isModel () {
    return this.__typename === this.constructor.name
  }

  revert () {
    this.setData(this.deepCloneDataWorker(this.originalData, false, false, []))
  }

  async reset () {
    if (this.querySubscription) {
      this.querySubscription.unsubscribe()
    }

    this.id = null

    // Remove existing data
    const toDestroy = []
    const defaultData = this.defaultData
    if (this.data) {
      _.each(this.allSpecifiedFieldNames, (fieldName) => {
        // Key doesn't exist so we won't be overwritting
        if (!this.data[fieldName]) return

        if (defaultData[fieldName]) return // Default exists so we will be setting it later

        // Only destroy models we own!
        this.data[fieldName].destroy && this.data[fieldName].hasAncestor(this) && !this.data[fieldName].hasVueParent && toDestroy.push(this.data[fieldName])
      })
    }

    // Set up as new model
    this.setData(defaultData, true)

    // Trigger watchers to rerun, needed for watchers that set initial values
    this.watcherRecomputeId += 1

    this.loaded = true
    this.loading = false

    await nextTick()
    _.each(toDestroy, model => {
      model.destroy()
    })
  }

  resetField (name) {
    this[name] = null
    this.originalData[name] = null
    this.originalDataForTrackingChanges[name] = null
  }

  setSavePoint () {
    this.savePointData = this.deepCloneData()
  }

  restoreToSavePoint () {
    this.setData(_.cloneDeep(this.savePointData))
  }

  // Converts the given data, and it's nested children, into modeljs objects
  // data is not cloned! Make a clone of data before adding it if you don't want it added by reference
  setData (data, isOriginalData = false) {
    this.settingData = true
    // TODO INDY-3193: we should probably throw an error if attempting to set data of a language different from this.language
    if (this.isCollection) {
      this.setCollectionData(data)
    } else {
      this.setObjectData(data)
    }

    if (isOriginalData) {
      // Create a copy of the initial data
      this.originalData = this.deepCloneData(true)
      this.originalDataForTrackingChanges = this.deepCloneData(true, true)
    }
    this.settingData = false
  }

  handleSubmodelSave () {
    this.originalData = this.deepCloneData(true)
    this.originalDataForTrackingChanges = this.deepCloneData(true, true)
  }

  setObjectData (data = {}) {
    data = this.parse(data)

    if (!data) {
      data = {}
    }

    // Don't include typename as part of the data
    delete data.__typename

    // Assign a local ID to this object
    if (!this.noId && !data.id && !this.id) {
      data.id = this.nextLocalId()
    }

    // Do deep model matching
    this.attachAndInflateModel(data)

    if (this.afterInflate) {
      this.afterInflate()
    }
  }

  nextLocalId () {
    return nextLocalId()
  }

  setCollectionData (data = []) {
    // Update language configuration early to correctly propagate to children/fields
    this.updateLanguageFromDataOrApp(data)
    this.inferForcedLanguageFromLatestLanguage()
    // Do deep model matching
    this.attachAndInflateCollection(data)
  }

  // Allow model to format incoming data
  parse (data) {
    return data
  }

  processPropertyVersions (data) {
    const keysToSkip = {}

    if (this.typeName === 'ClientConfig' && (data.circuitPropertyVersions || data.sitePropertyVersions)) {
      const objectFromData = (dataProperty) => {
        if (typeof dataProperty === 'string') {
          try {
            return JSON.parse(dataProperty)
          } catch (error) {
            console.error(`[PROPERTY VERSIONS] [ERROR] Error parsing property versions for [${this.typeName}]`, error)
            return {}
          }
        } else {
          return dataProperty || {}
        }
      }

      const newPropertyVersions = {
        ...objectFromData(data.circuitPropertyVersions),
        ...objectFromData(data.sitePropertyVersions)
      }
      const existingPropertyVersions = { ...this.data.sitePropertyVersions || {}, ...this.data.circuitPropertyVersions || {} }
      _.each(_.keys(newPropertyVersions), key => {
        if (existingPropertyVersions[key] && existingPropertyVersions[key] === newPropertyVersions[key]) {
          keysToSkip[_.camelCase(key)] = true
        }
      })
    }

    return keysToSkip
  }

  /**
   * @param {Model|ModelRecordRawData|null|undefined} data
   */
  async attachAndInflateModel (data) {
    // Support raw data and models
    if (data && data.data) {
      data = data.data
    }

    // Destroy old data that will be overwritten
    const toDestroy = []
    if (this.data) {
      const keysToSkip = this.processPropertyVersions(data)

      _.each(_.keys(data), key => {
        // Key doesn't exist so we won't be overwriting
        if (!this.data[key]) return

        // If these are objects and are identical references, don't destroy
        if (this.data[key] === data[key]) return

        if (this.typeName === 'ClientConfig' && keysToSkip[key]) {
          data[key] = this.data[key]
          return
        }

        // Only destroy models we own!
        this.data[key].destroy && this.data[key].hasAncestor(this) && !this.data[key].hasVueParent && toDestroy.push(this.data[key])
      })
    }

    let newData
    if (!this.data) {
      newData = {}
    } else {
      newData = this.data
    }

    // Check against data policies
    // Check for extra data and create list of keys to iterate through
    let keys
    if (this.extraDataPolicy === 'warn' || this.extraDataPolicy === 'error') {
      const extraKeys = _.without(_.keys(data), ...this.allSpecifiedFieldNames)
      if (extraKeys.length) {
        const message = `Unexpected data was provided to [${this.typeName}] (${extraKeys.join(', ')})`
        if (this.extraDataPolicy === 'warn') {
          console.warn(`[WARN] ${message}`)
        } else if (this.extraDataPolicy === 'error') {
          throw new Error(message)
        } else {
          throw new Error('Unhandled Condition')
        }
      }
    }
    if (this.extraDataPolicy === 'infer' || this.extraDataPolicy === 'warn' || this.extraDataPolicy === 'error') {
      keys = _.intersection(_.keys(data), _.keys(this.fields))
    } else if (this.extraDataPolicy === 'ignore') {
      keys = _.intersection(_.keys(data), this.allSpecifiedFieldNames)
    } else {
      throw new Error('Unhandled Condition')
    }

    // Check for missing data
    let backfillMissingData = false
    if (this.missingDataPolicy !== 'ignore') {
      const missingKeys = _.without(this.allSpecifiedFieldNames, ..._.keys(data))
      if (missingKeys.length) {
        const message = `Missing data for [${this.typeName}]. Missing fields (${missingKeys.join(', ')}) got (${_.keys(data).join(', ')}) expected (${this.allSpecifiedFieldNames.join(', ')})`
        if (this.missingDataPolicy === 'warn') {
          console.warn(`[WARN] ${message}`)
        } else if (this.missingDataPolicy === 'error') {
          throw new Error(message)
        } else if (this.missingDataPolicy === 'backfill') {
          console.log(`Fetching missing data for [${this.typeName}] will backfill. Fetching fields (${missingKeys.join(', ')}). Received fields (${_.keys(data).join(', ')})`)
          backfillMissingData = true
        } else {
          throw new Error('Unhandled Condition')
        }
      }
    }
    // Update language configuration early to correctly propagate to children/fields
    this.updateLanguageFromDataOrApp(data)
    this.inferForcedLanguageFromLatestLanguage()

    const orderedKeys = _.intersection(_.keys(this.fields), keys)
    _.each(orderedKeys, (fieldName) => {
      // Already inflated
      if (data[fieldName] && data[fieldName].isModel) {
        if (!data[fieldName].parent && !this.hasAncestor(data[fieldName])) {
          data[fieldName].setParent(this)
        }
        newData[fieldName] = data[fieldName]
        return
      }

      // See what we got
      const { Type, isCollection, field } = this.fieldType(fieldName)

      // Handle null values
      if (data[fieldName] === null && !(field.mergeDefault && field.default)) {
        newData[fieldName] = null
        return
      }

      // Check for mismatching data type
      if (data[fieldName] && data[fieldName].typeName && data[fieldName].typeName !== Type.name) {
        // debugger
      }

      if (isSimpleType(Type)) {
        // Check for custom parser
        let newFieldData
        if (Type.parse) {
          if (isCollection) {
            newFieldData = _.map(data[fieldName], datum => {
              if (Type === LocalizedJsonObject) {
                return Type.parse(datum, field.translatables, this)
              } else {
                return Type.parse(datum)
              }
            })
          } else if (Type === LocalizedJsonObject) {
            newFieldData = Type.parse(data[fieldName], field.translatables, this)
          } else {
            newFieldData = Type.parse(data[fieldName])
          }
        } else {
          newFieldData = data[fieldName]
        }

        // Check for defaults
        if (field.mergeDefault && (field.type === Object || field.type === JsonObject || field.type === LocalizedJsonObject) && field.default) {
          let newReactiveFieldData = this.createReactive({ ...field.default(), ...newFieldData }, this.options.nonReactive)
          if (field.type === LocalizedJsonObject) {
            // Since we are creating a new object, we need to ensure the new localized json object is proxied for translation keys
            newReactiveFieldData = LocalizedJsonObject.parse(newReactiveFieldData, field.translatables, this)
          }
          newData[fieldName] = newReactiveFieldData
        } else if (newFieldData === null || newFieldData === undefined) {
          if (field.default) {
            newData[fieldName] = _.isFunction(field.default) ? field.default() : field.default
          } else {
            newData[fieldName] = newFieldData
          }
        } else {
          newData[fieldName] = this.createReactive(newFieldData, this.options.nonReactive)
        }
        return
      }
      if (isPrimitive(Type)) {
        if (Type.parse) {
          if (isCollection) {
            newData[fieldName] = _.map(data[fieldName], datum => Type.parse(datum))
          } else {
            newData[fieldName] = Type.parse(data[fieldName])
          }
        } else {
          newData[fieldName] = data[fieldName]
        }
        return
      }

      // Construct model or collection
      let options = {
        parent: this,
        key: fieldName,
        baseQuery: false,
        nonReactive: this.options.nonReactive,
        isCollection,
        forceLanguage: this.options.forceLanguage
      }
      if (isCollection) {
        options.structure = this.options.structure[fieldName] ? this.options.structure[fieldName][0] : true
        options = _.extend({}, this.options[fieldName], options)
        // The collection will attach to this[fieldName] automatically and maintain that reference
        newData[fieldName] = new Type(data[fieldName], options)
        newData[fieldName].loaded = true
      } else {
        options.structure = this.options.structure[fieldName]
        newData[fieldName] = new Type(data[fieldName], options)
        newData[fieldName].loaded = true
      }
    })

    if (!this.data) {
      // This will only happen the first time. The data object will never changed so references to this will be preserved
      this.data = this.createReactive(newData, this.options.nonReactive)
    }

    if (backfillMissingData) {
      this.fetch()
    }

    await nextTick()
    _.each(toDestroy, model => {
      model.destroy()
    })

    if (this.afterAttach) {
      this.afterAttach()
    }
  }

  /**
   * TODO Update to handle nested collections
   * @param {Array<Model|ModelRecordRawData>|null|undefined} data
   */
  attachAndInflateCollection (data) {
    const Type = this.constructor

    const newData = _.map(data, (modelData, index) => {
      if (modelData === null || modelData === undefined) {
        return modelData
        // Already inflated
      } else if (modelData.isModel) {
        modelData.language = this.language
        modelData.previewing = this.previewing
        modelData.options.forceLanguage = this.options.forceLanguage
        return modelData
        // Primitive data
      } else if (isPrimitive(Type)) {
        if (Type.parse) {
          return Type.parse(modelData)
        } else {
          return modelData
        }
        // Raw data, construct model
        // Primitive data
      } else if (isSimpleType(Type)) {
        return modelData
      } else {
        const options = _.extend({}, this.options, {
          parent: this,
          key: index,
          baseQuery: false,
          nonReactive: this.options.nonReactive,
          isCollection: false,
          forceLanguage: this.options.forceLanguage
        })
        // Set this as an associated type if there is a match to the parent
        const associatedFieldName = this.parent && _.find(_.keys(this.fields), fieldName => this.fields[fieldName].type.name === this.parent.typeName)
        if (associatedFieldName && !_.includes(_.keys(modelData))) {
          modelData[associatedFieldName] = this.parent
          modelData[associatedFieldName].previewing = this.previewing
        }
        const newSubModelInstance = new Type(modelData, options)
        newSubModelInstance.language = this.language
        newSubModelInstance.previewing = this.previewing
        return newSubModelInstance
      }
    })

    this.setAndTriggerCollectionChange(newData)
  }

  /**
   * @param {ModelCollectionRawData} newData
   */
  async setAndTriggerCollectionChange (newData) {
    // Do this strange thing because the array can't be shallow reactive and we want to allow other things that reference this array to maintain the same reference
    // so we clear it, unset it from this model, and then use Vue.set to set it again. This is the only thing about modifying an array that will trigger a change.
    // It would be much better to have a way to do a shallow reactive array and let this happen anytime anything is modified about the array
    // const data = this.data || []
    // data.length = 0
    // data.push(...newData)

    // Need to unset and set to trigger a change, even though we are setting it with the same reference,
    // the array has changed and this will trigger the change
    const toDestroy = _.without(this.data, ...newData)

    this.data = []
    this.data = this.sortNewData(newData)

    // Update ID-indexed lookup map for configuration collections
    if (this.constructor.isConfiguration) {
      this._dataById = _.keyBy(this.data, 'id')
    }

    // Destroy after references have been replaced
    await nextTick()
    _.each(toDestroy, model => {
      model.destroy && model.destroy()
    })
  }

  fieldType (name) {
    return this.constructor.fieldType(name)
  }

  static fieldType (name) {
    const field = this.fields[name]

    if (!field) {
      throw new Error(`Unknown field [${name}] on [${this.typeName}]`)
    }

    const isCollection = _.isArray(field.type)
    let Type
    if (isCollection) {
      Type = field.type[0]
    } else {
      Type = field.type
    }

    const isConfigurationField = !!this.configurationFields[name]
    const isConfigurationIdField = !!this.configurationIdFields[name]
    let isTranslatableField = field.translatable === true
    if (_.includes(TRANSLATABLE_SUB_KEY_TYPES, Type.name)) {
      // For the JSON field types, we consider them translatable if they have at least one translatableKey.
      // Note that this function doesn't check whether a specific key is translatable, that's checked by
      // the ModelFormFieldMixin.
      isTranslatableField = _.isArray(field.translatables) && !_.isEmpty(field.translatables)
    }

    return { isCollection, Type, field, isConfigurationField, isConfigurationIdField, isTranslatableField }
  }

  json () {
    return JSON.stringify(this.deepCloneData(false, false, true))
  }

  // Create a clone of the data structure. This returns a tree of Models and Collections (not JSON!) with all non-reference data copied.
  // Reference data is not cloned but the collections that point to reference data is cloned. This returned data is not
  deepCloneData (remoteOnly = false, forChangeTracking = false, noReferences = false) {
    return this.deepCloneDataWorker(this.data, remoteOnly, forChangeTracking, [], noReferences)
  }

  deepCloneDataWorker (data, remoteOnly, forChangeTracking, visited, noReferences) {
    if (this.options.isCollection) {
      return this.deepCloneArrayData(this.constructor, data, remoteOnly, forChangeTracking, visited, noReferences)
    } else {
      return this.deepCloneObjectData(data, remoteOnly, forChangeTracking, visited, noReferences)
    }
  }

  deepCloneObjectData (data, remoteOnly, forChangeTracking, visited, noReferences) {
    const clonedData = {}
    _.each(_.keys(data), (fieldName) => {
      const value = data[fieldName]

      const { Type, isCollection } = this.fieldType(fieldName)

      const field = this.fields[fieldName]

      if (remoteOnly) {
        // Only encode remote data
        if (field && (field.local || field.output) && field.trackChanges !== true) {
          return
        }
      }

      if (field && field.excludeFromClone && noReferences) {
        return
      }

      if (!value) {
        clonedData[fieldName] = value
      } else if (isPrimitive(Type)) {
        clonedData[fieldName] = value
      } else if (field && field.reference && !(field.acceptsNestedAttributes && this.options.editing) && !noReferences) {
        // Skip cloning reference types, either use their ids or a reference
        if (value && isCollection) {
          if (forChangeTracking) {
            clonedData[fieldName] = _.map(value.data, 'id')
          } else {
            // Shallow clone collections to track reference changes
            clonedData[fieldName] = [...(value.data || value)]
          }
        } else {
          if (forChangeTracking) {
            clonedData[fieldName] = value.id
          } else {
            clonedData[fieldName] = value
          }
        }
      } else if (isSimpleType(Type) || !value.isModel) {
        if (forChangeTracking) {
          clonedData[fieldName] = _.cloneDeepWith(value, object => {
            if (!object || !object.isModel) return undefined // Undefined invokes the default cloneWith

            if (object.isCollection) {
              return _.map(object.data, 'id')
            } else {
              return object.id
            }
          })
        } else {
          clonedData[fieldName] = _.cloneDeepWith(value, object => (object && object.isModel) ? object : undefined) // Undefined invokes the default cloneWith
        }
      } else if (isCollection || value.isModel) {
        // Handle models and collections

        // Check to see if we have already visited this model or collection. If we this is a recursive reference and can be skipped
        const key = [this.typeName, this.id].join(',')
        if (_.includes(visited, key)) {
          return
        }

        visited.push(key)
        if (isCollection) {
          clonedData[fieldName] = this.deepCloneArrayData(Type, value, remoteOnly, forChangeTracking, visited, noReferences)
        } else {
          clonedData[fieldName] = value.deepCloneDataWorker(value.data, remoteOnly, forChangeTracking, visited, noReferences)
        }
        visited.pop()
      } else {
        throw new Error('Unhandled Condition')
      }
    })
    return clonedData
  }

  deepCloneArrayData (Type, array, remoteOnly, forChangeTracking, visited, noReferences) {
    const clonedData = []
    if (array.data) {
      array = array.data
    }
    _.each(array, (model) => {
      if (isPrimitive(Type)) {
        clonedData.push(model)
      } else if (isSimpleType(Type)) {
        clonedData.push(_.cloneDeepWith(model, object => (object && object.isModel) ? object : undefined)) // Undefined invokes the default cloneWith)
      } else if (!model.isModel) {
        clonedData.push(_.cloneDeepWith(model, object => (object && object.isModel) ? object : undefined)) // Undefined invokes the default cloneWith)
      } else {
        clonedData.push(model ? model.deepCloneDataWorker(model.data, remoteOnly, forChangeTracking, visited, noReferences) : model)
      }
    })
    return clonedData
  }

  // Use this model as a proxy for 'original'. Will assume values of 'original' and can apply settings to 'original'
  proxy (original) {
    this.reset()
    if (this.isTranslationEditingAllowed && original.language !== this.language) {
      // resetToLanguage() doesn't reset ID so we do this after reset()
      this.resetToLanguage(original.language)
    }
    this.setData(original.deepCloneData(), true)
    if (original.options.parent) {
      this.setParent(original.options.parent, false)
    }
    if (original.options.forceLanguage) {
      this.options.forceLanguage = original.options.forceLanguage
    }
    this.options.baseQuery = original.options.baseQuery
    this.originalObject = original
  }

  resetToLanguage (language) {
    if (this.options.forceLanguage === language) return

    const isCurrentUserAdmin = this.context?.currentUser?.admin
    if (!this.isTranslationEditingAllowed || !isCurrentUserAdmin) {
      throw new Error('Model language override not allowed')
    }
    if (this.isCollection && this.isSubmodel) {
      throw new Error('Can only set language on individual models or base collections')
    }
    language = languageFromLocale(language)
    if (language && !_.includes(REGION_AVAILABLE_LOCALES, language)) {
      throw new Error(`Invalid language code: ${language}`)
    }

    if (!this.isCollection) {
      const id = this.id
      this.reset()
      this.id = id
    }
    if (!language || language === EN) {
      this.options.forceLanguage = null
    } else {
      this.options.forceLanguage = language
    }
  }

  /**
   * Ensures the forceLanguage option is set/unset as necessary to for non-English sub-models
   * to save to their language. This should ONLY be used for Admin, since it's able to edit translations.
   * Otherwise other app modes may get wrong language data because we skip certain language checks.
   */
  inferForcedLanguageFromLatestLanguage () {
    if (!this.isTranslationEditingAllowed) return

    if (this.language === EN) {
      this.options.forceLanguage = null
    } else {
      this.options.forceLanguage = this.language
    }
  }

  updateLanguageFromDataOrApp (newData) {
    // This should be the usual case in non-Admin modes. On app boot, I18n is loaded after this file
    // so we handle Model.app.i18n being missing. This only happens at the very start
    const appLanguage = Model.app.i18n?.localeLanguage || EN
    if (!this.isTranslationEditingAllowed) {
      this.language = appLanguage
      return this.language
    }

    // This should be the usual case in Admin mode.
    if (newData._lang) {
      this.language = newData._lang
      return this.language
    }

    // We shouldn't get this far following a fetch, though it could happen when manually setting
    // ad-hoc data. If new data doesn't have a language, we'll try to infer it.
    let translatableChildData
    if (this.options.isCollection) {
      translatableChildData = _.find(newData, model => _.isObject(model) && (model._lang || model.data?._lang))
    }
    if (this.parent?.language) {
      this.language = this.parent.language
    } else if (translatableChildData) {
      this.language = translatableChildData._lang || translatableChildData.data?._lang
    } else if (this.options.forceLanguage) {
      this.language = this.options.forceLanguage
    } else if (!this.language) {
      this.language = appLanguage
    }
    return this.language
  }

  /** Guard against Admin (only) app accidentally saving content for the wrong language. */
  validateLanguageForMutation () {
    if (!this.isTranslationEditingAllowed) return true

    const isTargetingOrDefaultingToEnglish = this.language === EN && (!this.options.forceLanguage || this.options.forceLanguage === EN)
    if (isTargetingOrDefaultingToEnglish) return true
    const isTargetingSameLanguage = this.options.forceLanguage && this.options.forceLanguage === this.language
    if (isTargetingSameLanguage) return true

    if (!this.isTranslatable) {
      throw new Error('Model is not translatable')
    } else if (!this.options.forceLanguage) {
      throw new Error('The forceLanguage option is required to save model translations')
    } else {
      throw new Error('The forceLanguage option must match the fetched language to save model translations')
    }
  }

  clone () {
    return new this.constructor(this.deepCloneData(), this.options)
  }

  copy (skipWatchers = false) {
    let options = this.options
    if (skipWatchers) {
      options = Object.assign({}, this.options, { skipWatchers: true })
    }
    const copy = new this.constructor(this.deepCloneData(), options)
    copy.id = this.nextLocalId()

    copy.options.parent = null
    copy.options.copiedFromId = this.id

    return copy
  }

  setRelated (name, data) {
    if (!this.fields[name]) throw new Error('Unknown field: ' + name)

    const Type = this.fields[name].type
    const newModel = new Type((data && data.data) || data, this.options[name])

    const newData = {}
    newData[name] = newModel
    this.setData(newData, true)
    newModel.loaded = true

    return newModel
  }

  rawAccess (fieldName) {
    const value = this[fieldName]

    return value
  }

  fieldOptions (name) {
    if (!this.fields[name]) throw new Error('Unknown field: ' + name)
    if (!this.fields[name].options) throw new Error('No options defined for field: ' + name)

    const options = this.fields[name].options

    if (!_.isArray(options)) throw new Error('Unhandled option types')

    // Return options if already an array of objects with label and value properties
    if (_.has(options[0], 'label') && _.has(options[0], 'value')) return options

    if (!_.isString(options[0])) throw new Error('Unhandled option types')

    return this.arrayToOptions(options)
  }

  arrayToOptions (options) {
    return _.map(options, option => { return { value: option, label: _.startCase(option) } })
  }

  get original () {
    return new this.constructor(this.originalData)
  }

  get asOptions () {
    if (!this.isCollection) {
      throw new Error(`Expected collection [${this.typeName}]`)
    }

    return _.map(this.data, model => { return { value: model.id, label: model.name } })
  }

  setParent (parent, errorOnCircular = true) {
    if (parent.hasAncestor(this)) {
      if (!errorOnCircular) return

      const path = []

      path.push(`${parent.typeName}${parent.isCollection ? 'Collection' : ('.' + parent.id)}`)

      let grandParent = parent.parent
      while (grandParent !== this) {
        path.push(`${grandParent.typeName}${grandParent.isCollection ? 'Collection' : ('.' + grandParent.id)}`)
        grandParent = grandParent.parent
      }

      path.push(`${this.typeName}${this.isCollection ? 'Collection' : ('.' + this.id)}`)

      console.warn('[WARNING] Cannot set parent, the parent object has this an ancestor: ' + path.join(' -> '))
      return
    }

    this.options.parent = parent
    this.options.forceLanguage = parent.options.forceLanguage

    if (this.options.isCollection) {
      return
    }

    // See if the parent object matches the type of a field. If so, update that field to be the parent
    if (!this.hasAncestor(parent)) {
      return
    }

    if (parent && parent.options.isCollection) {
      parent = parent.options.parent
    }

    // See if there is a field of this type. If so update the value
    const fieldName = _.find(this.allSpecifiedFieldNames, (fieldName) => {
      const { Type, isCollection } = this.fieldType(fieldName)
      return parent && !isCollection && Type === parent.constructor
    })

    if (fieldName) {
      this.setData({ [fieldName]: parent })
    }
  }

  add (modelOrData, prepend = false) {
    if (!this.isCollection) {
      throw new Error('Tried to push to a model that is not a collection')
    }

    // See if the user passed in raw data or a model
    let model
    if (modelOrData.isModel) {
      model = modelOrData

      // Incoming model must match the structure of the collection it's being added to
      model.options.structure = this.options.structure
      model.options.extraDataPolicy = this.options.extraDataPolicy
      model.options.missingDataPolicy = this.options.missingDataPolicy

      // Set the data to check to see if there is anything missing and will backfill here if needed
      // Also need to update with default data because the new structure could be different
      // and we need to ensure reactivity and defaults
      const data = model.data
      model.data = null // Need to set data to null so that a new reactive object will be created
      model.setData(_.extend({}, this.defaultData, data))
    } else {
      const newModelOptions = _.clone(this.options)
      newModelOptions.isCollection = false
      model = new this.constructor(modelOrData, newModelOptions)
    }

    model.setParent(this)
    model.options.key = this.data.length

    const data = this.data

    if (prepend) {
      data.unshift(model)
    } else {
      data.push(model)
    }

    // Need to unset and set to trigger a change, even though we are setting it with the same reference,
    // the array has changed and this will trigger the change
    this.setAndTriggerCollectionChange(data)

    return model
  }

  addAll (models) {
    if (!this.isCollection) {
      throw new Error('Tried to push to a model that is not a collection')
    }

    const areAllModels = _.every(models, model => model.isModel)
    if (!areAllModels) {
      throw new Error('Tried to add an array of models that are not all models')
    }

    const modelsToAdd = _.map(models, model => {
      // Incoming model must match the structure of the collection it's being added to
      model.options.structure = this.options.structure
      model.options.extraDataPolicy = this.options.extraDataPolicy
      model.options.missingDataPolicy = this.options.missingDataPolicy

      // Set the data to check to see if there is anything missing and will backfill here if needed
      // Also need to update with default data because the new structure could be different
      // and we need to ensure reactivity and defaults
      const data = model.data
      model.data = null // Need to set data to null so that a new reactive object will be created
      model.setData(_.extend({}, this.defaultData, data))
      model.setParent(this)
      model.options.key = this.data.length
      return model
    })

    const data = [...this.data, ...modelsToAdd]
    this.setAndTriggerCollectionChange(data)
  }

  push (model) {
    this.add(model)
  }

  removeAll (models) {
    if (!this.isCollection) {
      throw new Error('Tried to remove from a model that is not a collection')
    }
    if (!Array.isArray(models)) {
      throw new Error('No array specified to remove all from collection')
    }

    _.each(models, model => {
      model.beforeRemove && model.beforeRemove()
      model.destroy()
    })

    this.setData(_.differenceWith(this.data, models, (originalData, dataToRemove) => originalData.id === dataToRemove.id))
  }

  // Call on a collection or on a model in a collection
  remove (model) {
    if (model && !this.isCollection) {
      throw new Error('Tried to remove a model that is not a collection')
    } else if (!model && !this.parent?.isCollection) {
      throw new Error('Tried to remove a model that is not a collection')
    }

    if (model) {
      model.beforeRemove && model.beforeRemove()
      model.destroy() // TODO We don't always want to do this, do we???
      this.setData(_.filter(this.data, (item) => item.id !== model.id))
    } else {
      this.parent.remove(this)
    }
  }

  toggle (model) {
    if (!this.isCollection) {
      throw new Error('Tried to toggle a model that is not a collection')
    }

    if (this.contains(model)) {
      this.remove(model)
    } else {
      this.add(model)
    }
  }

  get (id) {
    if (!this.isCollection) {
      throw new Error('Tried to find model by id, not collection')
    }

    // Use ID-indexed lookup map for configuration collections
    if (this.constructor.isConfiguration && this.dataById) {
      return this.dataById[id] || null
    }

    return _.find(this.data, { id })
  }

  get dataById () {
    // Return ID-indexed lookup map for configuration collections
    if (this.constructor.isConfiguration && this._dataById) {
      return this._dataById
    }
    return null
  }

  get last () {
    if (!this.isCollection) {
      throw new Error('Tried to toggle a model that is not a collection')
    }

    if (!this.data.length) {
      return null
    }

    return this.data[this.data.length - 1]
  }

  get first () {
    if (!this.isCollection) {
      throw new Error('Tried to toggle a model that is not a collection')
    }

    if (!this.data.length) {
      return null
    }

    return this.data[0]
  }

  get isFirst () {
    return this.findIndex === 0
  }

  get isLast () {
    return this.findIndex === this.parent?.data.length - 1
  }

  get findIndex () {
    if (!this.parent || !this.parent.data) {
      return -1
    }
    return _.findIndex(this.parent?.data, model => model?.id === this.id)
  }

  get next () {
    return this.parent.data[this.findIndex + 1]
  }

  get previous () {
    return this.parent.data[this.findIndex - 1]
  }

  contains (model) {
    if (!this.isCollection) {
      throw new Error('Tried to check for contains on a model that is not a collection')
    }

    // Use ID-indexed lookup map for configuration collections
    if (this.constructor.isConfiguration && this.dataById) {
      return !!this.dataById[model.id]
    }

    return _.some(this.data, item => item?.id === model.id)
  }

  find (evaluator) {
    if (!this.isCollection) {
      throw new Error('Tried to filter on a model that is not a collection')
    }

    return _.find(this.data, evaluator)
  }

  filter (evaluator) {
    if (!this.isCollection) {
      throw new Error('Tried to filter on a model that is not a collection')
    }

    this.setData(_.filter(this.data, evaluator))
  }

  // Supports scalars or prioritized array of scalars
  comparator (a, b) {
    a = _.isArray(a) ? a : [a]
    b = _.isArray(b) ? b : [b]
    for (let index = 0; index < a.length; index += 1) {
      if (a[index] > b[index]) return 1
      if (a[index] < b[index]) return -1
    }
    return 0
  }

  sortNewData (newData) {
    if (!this.isCollection) {
      throw new Error('Tried to sort on a model that is not a collection')
    }

    if (this.sortOrder === 'natural') {
      return newData
    }

    Array.prototype.sort.apply(newData, [(a, b) => this.comparator(this.sortValue(a), this.sortValue(b))])
    return newData
  }

  sort () {
    if (!this.isCollection) {
      throw new Error('Tried to sort on a model that is not a collection')
    }

    if (this.sortOrder === 'natural') {
      return
    }

    this.setAndTriggerCollectionChange(this.data)
  }

  // Override to customize sorting
  sortValue (model) {
    if (this.sortOrder === 'natural') {
      return null
    } else if (this.sortOrder === 'display-order') {
      return model.displayOrder
    } else if (this.sortOrder === 'creation') {
      // By remote creation order
      const remoteOrder = (model.id > 0) ? model.id : Number.MAX_SAFE_INTEGER

      // By local creation order
      const localOrder = (model.id < 0) ? -model.id : Number.MAX_SAFE_INTEGER

      return [remoteOrder, localOrder]
    } else if (this.sortOrder === 'custom' && this.customSortValue) {
      return this.customSortValue(model)
    }
  }

  validateDisplayOrder (orderField = 'displayOrder') {
    if (!this.isCollection) {
      throw new Error('Tried to validate display order on a model that is not a collection')
    }
    if (!this.fields[orderField]) {
      throw new Error('Tried to validate display order on a model that does not have display order field')
    }

    _.each(this.data, (model, index) => { model[orderField] = index + 1 })
  }

  applyChanges () {
    if (!this.originalObject) {
      return
    }

    const newData = this.deepCloneDataWorker(this.originalObject.data, false, false, [], false)
    _.each(_.keys(this.data), (key) => {
      const { field } = this.fieldType(key)

      // We should not apply changes to fields that are not requested in the original object
      // This is to prevent destroying data unintentionally, as the child may still require reactivity for that field
      if (!this.originalObject.options.structure[key]) return

      // Skip fields that are not directly models or collections, but hold references to them
      // This can be used to prevent unintentional destruction of data, such as a local Array of models used to do some computation
      if (field.skipProxyPropagation) return

      if (field.output) return

      newData[key] = this.data[key]
    })

    this.originalObject.setData(this.deepCloneDataWorker(newData, false, false, [], false))
  }

  /**
   * Returns the data that is sent to the API to write to the database.
   * Removes local, output, and fields with masked values.
   * @param {Record<string, any>} originalData
   * @returns {Record<string, any>}
   */
  persistenceData (originalData = this?.originalData) {
    // See if we should mark anything for destruction
    if (this.isCollection) {
      if (!this.parent || !this.parent.fields[this.key] || !this.parent.fields[this.key].allowDestroy) {
        return this.data
      }

      // Prevent destroyIds are used for batch saving to prevent unintentional destruction (See BookingWeek model for batch save example)
      const keepIds = _.concat(_.map(this.data, 'id'), this.preventDestroyIds)
      const toDestroy = _.filter(originalData, model => model?.id && model.id > 0 && !_.includes(keepIds, model.id))
      const destroyData = _.map(toDestroy, model => { return { id: model.id, _destroy: true } })

      return _.concat(destroyData, this.data)
    }

    const data = _.clone(this.encode())

    const encodedData = {}
    _.each(data, (value, fieldName) => {
      const field = this.fields[fieldName]

      // No field specification, this came from special user encoding, just accept the value
      if (!field) {
        encodedData[fieldName] = value
        return
      }

      const skipEncodeing = field.local || field.output || this.isFieldMaskedByApi(fieldName)
      if (skipEncodeing) {
        return
      }

      if (field.encode) {
        field.encode.call(this, encodedData, fieldName, value)
      } else if (field.type.encode) {
        encodedData[fieldName] = field.type.encode(value)
      } else {
        encodedData[fieldName] = value
      }
    })

    return encodedData
  }

  // Allow user formatting of data prior to saving
  encode () {
    let data = this.data
    if (this.isLocal) {
      // Local models are not saved, add identifiers to the data so the API can determine if it's a new or existing record
      // This is to handle cases where we think the model is local due to a failed save, but the server actually saved it.
      data = _.extend(data, { localId: this.id, sessionIdentifier: getCurrentSessionIdentifier() })
    }

    return data
  }

  // Recursive call of encode
  get deepEncode () {
    const data = traverse(this.persistenceData(), 'persistenceData', true, this.originalData)
    return traverseProcess(data, withoutLocalIds)
  }

  fetch = async (force = false) => {
    this.loading = true

    let variables

    if (this.isCollection || this.options.variables) {
      variables = { ...this.options.variables }
    } else if (this.id && this.id > 0) {
      variables = { id: this.id }
    }

    // Add atlas parameter if the model has atlas property set
    if (this.atlas && !this.isCollection) {
      variables.atlas = true
    }

    // If the model is validatable, we have a version and data from a prior query
    // we can validate the result instead of just requesting.
    //
    // If the server's version matches ours, we will get confirmation or new data
    if (this.isCollection && this.validatable) {
      if (this.resultVersion) {
        console.log(`[CACHE] Validatable query, local version: ${this.resultVersion}, ${this.options.queryName}`)
        variables.resultVersion = this.resultVersion
      } else {
        console.log(`[CACHE] Validatable query, no existing version, ${this.options.queryName}`)
        variables.resultVersion = null
      }
    }

    if (!this.isCollection && _.includes(_.keys(variables), 'id') && (!variables.id || variables.id < 0)) {
      // id is null, so reset instead of fetch
      this.reset()
      return { data: this.deepCloneData(), errors: [] }
    }

    if (this.options.mergeChangesPostFetch) {
      const prefetchData = this.deepCloneDataWorker(this.data, true, false, [])
      this.prefetchChanges = changes(_.omit(prefetchData, this.mergeFieldsToOmit), _.omit(this.originalData, this.mergeFieldsToOmit), 'id')
    } else {
      this.prefetchChanges = null
    }

    const queryName = this.options.queryName || this.variableName
    try {
      const { query, response } = await this.executeQuery(queryName, variables, { ...this.options, paginate: this.isCollection && this.paginate }, force)
      return { query, response }
    } catch (e) {
      console.log(`Execute Query Failed. Query name: ${queryName}`, e)
      if (typeof Rollbar !== 'undefined') {
        // eslint-disable-next-line no-undef
        Rollbar.error('Execute Query Failed', e, { queryName, variables })
      }
    }
  }

  async ensureLoaded (retry = false, notify = false) {
    if (this.loaded) return

    if (notify) {
      window.dataLoadRetry = (window.dataLoadRetry || 0) + 1
    }

    // If we haven't started the fetch, do so now
    if (!this.loading) {
      if (this.typeName === 'ClientConfig') {
        this.validate()
        // Enable to log the query and response for updating the unit test mocks
        // .then(({ query, response }) => {
        //   console.log(`client-config-query-${appMode}`, query)
        //   console.log(`client-config-response-${appMode}`, response?.data)
        // })
      } else if (this.typeName === 'ClientContext') {
        this.fetch()
        // Enable to log the query and response for updating the unit test mocks
        // .then(({ query, response }) => {
        //   console.log(`client-context-query-${appMode}`, query)
        //   console.log(`client-context-response-${appMode}`, response?.data)
        // })
      } else {
        this.fetch()
      }
    }

    // If retry, set up watcher
    if (retry) {
      let retryCount = 0
      const stopWatchLoading = this.createWatcher(
        () => this.loading,
        async () => {
          if (this.loading) return

          if (this.loaded) {
            stopWatchLoading()
          } else if (retry) {
            retryCount += 1
            if (notify) {
              Notify.create({
                message: 'We are having trouble reaching the server. Please hold...',
                position: 'top',
                spinner: true
              })
            }

            if (retryCount > MAX_RETRY_COUNT) {
              // Give up
              console.log('Gave up retrying to connect to server')
              return
            }

            setTimeout(() => {
              // Try again
              this.ensureLoaded(false, notify)
            }, RETRY_DELAY_MS)
          }
        }
      )
    }

    return new Promise(resolve => {
      const stopWatchLoaded = this.createWatcher(
        () => this.loaded,
        async () => {
          window.dataLoadRetry = null
          stopWatchLoaded()
          resolve()
        }
      )
    })
  }

  nextRequestId () {
    this.requestId = (this.requestId || 0) + 1

    return this.requestId
  }

  queryResolveListeners = []
  toUnsubscribeOnNextResult = []

  handleQueryResolution (error = null, query = null, response = null) {
    // Always mark the model as loaded
    this.loading = false

    // Process the success or error
    _.each(this.queryResolveListeners, listener => {
      if (error) {
        listener.reject(error)
      } else {
        listener.resolve({ query, response })
      }
    })

    // Process all the necessary unsubscribes
    _.each(this.toUnsubscribeOnNextResult, subscriber => subscriber.unsubscribe())

    // Start over and reset all the listeners
    this.queryResolveListeners = []
    this.toUnsubscribeOnNextResult = []

    this.evictApolloCacheLastExecutedQuery()
  }

  // We either destroying a model or re-executing a query, we should evict the last executed query from the cache
  // This is to prevent the cache from growing over time and consuming memory
  evictApolloCacheLastExecutedQuery () {
    if (!_.isNil(this.lastExecutedQuery)) {
      try {
        Model.apollo.cache.evict({
          id: 'ROOT_QUERY',
          fieldName: this.lastExecutedQuery.name,
          args: this.lastExecutedQuery.variables
        })
      } catch (e) {
        console.warn('cache eviction failed', e.message)
        // Best to not log this to rollbar as it may be a common occurrence and create overhead as this is executed frequently
        // this.$rollbar.error('Cache eviction failed', e, { queryName: this.lastExecutedQuery.name, variables: this.lastExecutedQuery.variables })
      }
    }

    this.lastExecutedQuery = this.currentExecuteQuery
  }

  currentExecuteQuery = null
  lastExecutedQuery = null

  executeQuery = async function (name, variables, options, force = false) {
    // TODO catch exceptions and add to errors

    const queryOptions = {
      ...options,
      paginate: this.isCollection && this.paginate,
      validatable: this.isCollection && this.validatable
    }

    const query = gql(buildQuery(name, variables, queryOptions))

    // console.log('QUERY')
    // console.log(buildQuery(name, variables, queryOptions))
    // console.log({
    //   variables,
    //   fetchPolicy: force ? 'network-only' : 'cache-first'
    // })
    const requestId = this.nextRequestId()

    let promiseResolved = false
    return new Promise((resolve, reject) => {
      this.queryResolveListeners.push({ resolve, reject })

      if (this.options.watchQuery === false) {
        const errors = []
        Model.apollo.query({
          query,
          variables,
          fetchPolicy: force ? 'network-only' : 'cache-first'
        }).then(result => {
          let data
          if (this.isCollection && this.paginate) {
            data = _.cloneDeep(result.data[name].data)
            this.count = result.data[name].count
            this.isMore = result.data[name].isMore
          } else {
            data = _.cloneDeep(result.data[name])
          }

          this.processFetchedData(data, variables, errors)

          this.handleQueryResolution(null, query?.loc?.source?.body, result)
        }).catch(error => {
          this.handleQueryResolution(error)
        })
      } else { // watchQuery
        if (this.querySubscription) {
          this.toUnsubscribeOnNextResult.push(this.querySubscription)
        }
        this.querySubscription = Model.apollo.watchQuery({
          query,
          variables,
          fetchPolicy: force ? 'network-only' : 'cache-first'
        }).subscribe({
          next: result => {
            // Track the last used query and variables
            this.currentExecuteQuery = { name, variables }

            if (this.silent) {
              console.log('[FETCH] Silent, ignoring')
              this.handleQueryResolution(null, query?.loc?.source?.body, result)
              return
            }

            if (this.resultId > requestId) {
              console.log('[FETCH] There is already a more recently requested result, ignoring')
              return
            }

            if (requestId < this.requestId) {
              console.log('[FETCH] Warning, received result after a newer request was made')
            }

            let data
            if (this.isCollection && this.paginate) {
              data = _.cloneDeep(result.data[name].data)

              if (this.validatable && variables.resultVersion && variables.resultVersion === result.data[name].resultVersion) {
                console.log(`[CACHE] Validated, ${this.options.queryName}, Local Version is Current: ${this.resultVersion}`)
                if (!promiseResolved) {
                  promiseResolved = true
                  this.handleQueryResolution(null, query?.loc?.source?.body, result)
                }
                return
              } else if (this.validatable && !result.data[name].resultVersion && result.data[name].data) {
                console.log(`[CACHE] New data no version, ${this.options.queryName}, ${this.data.length} ${result.data[name].data.length}`)

                this.resultVersion = result.data[name].resultVersion
              } else if (this.validatable && result.data[name].resultVersion && result.data[name].data) {
                console.log(`[CACHE] New data with version, ${this.options.queryName}, Current Version: ${this.resultVersion || '<none>'}, New Result Version: ${result.data[name].resultVersion}, ${this.data.length} ${result.data[name].data.length}`)

                this.resultVersion = result.data[name].resultVersion
              } else if (this.validatable && !result.data[name].data) {
                console.log(`[CACHE] Versions not matching, but no data, ${this.options.queryName}, Current Version: ${this.resultVersion || '<none>'}, New Result Version: ${result.data[name].resultVersion} ${this.data.length}`)
                if (!promiseResolved) {
                  promiseResolved = true
                  this.handleQueryResolution(null, query?.loc?.source?.body, result)
                }
                return
              } else if (this.validatable) {
                console.log(`[CACHE] Unhandled, ${this.options.queryName}, Current Version: ${this.resultVersion || '<none>'}, New Result Version: ${result.data[name].resultVersion}`)
                if (!promiseResolved) {
                  promiseResolved = true
                  this.handleQueryResolution(null, query?.loc?.source?.body, result)
                }
                return
              }

              this.count = result.data[name].count
              this.isMore = result.data[name].isMore
            } else {
              data = _.cloneDeep(result.data[name])
            }

            this.resultId = requestId

            this.processFetchedData(data, variables, null)

            if (!promiseResolved) {
              promiseResolved = true
              this.handleQueryResolution(null, query?.loc?.source?.body, result)
            }
          },
          error: (error) => {
            this.processFetchedData(null, variables, [error])

            if (!promiseResolved) {
              promiseResolved = true
              if (this.gqlErrors && _.startsWith(this.gqlErrors[0].message, 'GraphQL error: User message: ')) {
                this.handleQueryResolution()
              } else {
                this.handleQueryResolution(error)
              }
            }
          }
        })
      }
    })
  }

  // TODO: finish implementation on backend
  // subscribeToRemoteChanges () {
  //   if (this.isCollection) {
  //     this.subscribeToRemoteCollectionChanges()
  //   } else {
  //     this.subscribeToRemoteModelChanges()
  //   }
  // }

  // subscribeToRemoteModelChanges () {
  //   if (this.observationResponse) {
  //     this.observationResponse.unsubscribe()
  //   }

  //   const resultQueryName = this.variableName
  //   const subscriptionName = 'modelUpdated'

  //   var variables = { typeName: this.typeName, id: this.id }

  //   const observer = Model.apollo.subscribe({
  //     query: gql(buildQuery(resultQueryName, variables, { ...this.options, subscriptionName: subscriptionName })),
  //     variables
  //   })

  //   this.observationResponse = observer.subscribe({
  //     next: (data) => {
  //       if (!data.data[subscriptionName] || !data.data[subscriptionName][this.variableName]) return

  //       this.setData(data.data[subscriptionName][this.variableName], true)
  //     },
  //     error (error) {
  //       console.error(error)
  //     }
  //   })
  // }

  // subscribeToRemoteListChanges () {
  //   if (this.observationResponse) {
  //     this.observationResponse.unsubscribe()
  //   }

  //   const resultQueryName = this.collectionVariableName
  //   const subscriptionName = 'listUpdated'

  //   var variables = { typeName: this.typeName, id: this.id }
  //   const observer = Model.apollo.subscribe({
  //     query: gql(buildQuery(resultQueryName, variables, { ...this.options, subscriptionName: subscriptionName })),
  //     variables
  //   })

  //   this.observationResponse = observer.subscribe({
  //     next: (data) => {
  //       if (!data.data[subscriptionName] || !data.data[subscriptionName][this.collectionVariableName]) return

  //       this.setData(data.data[subscriptionName][this.collectionVariableName], true)
  //     },
  //     error (error) {
  //       console.error(error)
  //     }
  //   })
  // }

  processFetchedData (data, _variables, errors) {
    this.loading = false

    if (errors && errors.length) {
      this.gqlErrors = errors
      return { data, errors }
    } else {
      this.loaded = true
      this.gqlErrors = null
    }

    if (data === null) {
      // Handle a reset, remove existing data
      _.each(this.allSpecifiedFieldNames, (fieldName) => {
        this[fieldName] = null
      })
      data = {}
    }

    this.options.baseQuery = true
    if (_.has(data, 'englishFallbackValues') && (!this.options.forceLanguage || this.options.forceLanguage === EN)) {
      data.englishFallbackValues = null
    }
    this.setData(data, true)

    if (this.prefetchChanges && !_.isEmpty(this.prefetchChanges)) {
      // TODO: we shouldn't be setting data again here, we should be merging the changes in the prefetchChanges
      this.setData(mergeById(this.deepCloneData(false), this.prefetchChanges))
    }

    Model.allBaseQueries.add(this)

    return { data, errors }
  }

  save = async (batchSaveCallback = null) => {
    this.validateLanguageForMutation()
    const options = {
      [this.variableName]: this.options,
      errors: {
        structure: {
          code: true,
          description: true,
          backtrace: true
        }
      }
    }

    // var presaveData
    // if (this.mergeChangesPostSave) {
    //   presaveData = this.deepCloneDataWorker(this.data, false, false, [])
    // }

    this.loading = true
    const { data, errors, query, response } = await executeMutation(`addOrUpdate${this.__typename}`, { [`${this.variableName}Input`]: this.deepEncode }, options)

    // If we are performing a custom batch save for a collection of models, we need to wait until all models have been saved before we rebuild the models
    // This is for performance reasons, we don't want to rebuild the models until all have been saved
    // If we encounter an error, we will still rebuild the models, but we will not trigger the batchSaveCallback
    if (!errors.length && batchSaveCallback && batchSaveCallback()) {
      // We use the batch save callback to ask the requesting model if more batch saves are needed
      return this.save(batchSaveCallback)
    }

    this.loading = false
    this.loaded = true

    // var postsaveData
    // if (this.mergeChangesPostSave) {
    //   postsaveData = this.deepCloneDataWorker(this.data, false, false, [])
    // }

    const results = data && data[this.variableName]

    if (results) {
      this.id = results.id
      this.options.baseQuery = true
      this.setData(results, true)
    }

    // if (this.mergeChangesPostSave) {
    //   console.log(changes(presaveData, postsaveData))
    // }

    // Check if this change is to a model used as part of the configuration
    if (this.constructor.isConfiguration && this.typeName !== 'ClientConfig') {
      if (!configurationStatus.batchSaving) {
        if (configurationStatus.automatic) {
          this.configuration.validateAndNotify()
        } else {
          this.configuration.validate()
        }
      }
    }

    return {
      errors,
      results,
      query,
      response
    }
  }

  delete = async () => {
    const variables = { id: this.id }
    variables.atlas = this.atlas || false

    const options = {
      [this.variableName]: this.options,
      errors: {
        structure: {
          code: true,
          description: true,
          backtrace: true
        }
      }
    }

    this.loading = true
    const { data, errors } = await executeMutation(`delete${this.__typename}`, variables, options)
    this.loading = false

    if (errors.length) {
      return { errors }
    }

    return {
      errors,
      data
    }
  }

  // if nothing is passed, duplicates current model, also can duplicate based on passed models or ids
  // TODO this could be static if called with arguments
  duplicate (modelsOrIds = null, atlas = null) {
    let ids

    if (!modelsOrIds) {
      ids = [this.id]
    } else if (modelsOrIds[0].isModel) {
      ids = _.map(modelsOrIds, 'id')
    } else {
      ids = modelsOrIds
    }

    const variables = { ids }

    if (atlas !== null) {
      variables.atlas = atlas
    } else if (this.atlasable) {
      variables.atlas = false
    }

    return executeMutation(
      `duplicate${this.collectionTypeName}`,
      variables,
      {
        errors: {
          structure: {
            code: true,
            description: true,
            backtrace: true
          }
        },
        [this.collectionVariableName]: {
          structure: {
            id: true
          }
        }
      }
    )
  }

  async duplicateModelToSite (libraryModelId, siteId, force = false) {
    const results = await executeMutation(
      `duplicate${this.typeName}ToSite`,
      { libraryModelId, siteId, force },
      {
        errors: {
          structure: {
            code: true,
            description: true
          }
        },
        [this.variableName]: {
          structure: {
            id: true
          }
        }
      }
    )

    return {
      errors: results.errors,
      [this.variableName]: results.data?.[this.variableName]
    }
  }

  get parent () {
    return this.options.parent
  }

  get isCollection () {
    return this.options.isCollection
  }

  get key () {
    return this.options.key
  }

  // e.g. orderItem
  static get variableName () {
    return downFirst(this.name)
  }

  get variableName () {
    return this.constructor.variableName
  }

  // e.g. OrderItemDefault
  static fragmentName (fragment = 'default') {
    return `${this.name}${upFirst(fragment)}`
  }

  // e.g. OrderItemDefault
  fragmentName (fragment = 'default') {
    return this.constructor.fragmentName(fragment)
  }

  get configuration () {
    return this.constructor.configuration
  }

  get context () {
    return this.constructor.context
  }

  get isTranslation () {
    return this.language !== EN
  }

  get i18n () {
    return this.constructor.app.i18n
  }

  get __typename () {
    return this.constructor.name
  }

  get typeName () {
    return this.constructor.name
  }

  // e.g. Order Items
  static get humanTypeName () {
    return _.startCase(this.name)
  }

  get humanTypeName () {
    return this.constructor.humanTypeName
  }

  static get humanCollectionName () {
    return this.constructor.humanTypeName
  }

  static get collectionTypeName () {
    return this.collectionVariableName[0].toUpperCase() + this.collectionVariableName.slice(1)
  }

  get collectionTypeName () {
    return this.collectionVariableName[0].toUpperCase() + this.collectionVariableName.slice(1)
  }

  get humanCollectionName () {
    return _.startCase(this.collectionVariableName)
  }

  // e.g. orderItems
  static get collectionVariableName () {
    return computeCollectionVariableName(this.variableName)
  }

  get collectionVariableName () {
    return this.constructor.collectionVariableName
  }

  static get configurationCollectionVariableName () {
    return this.collectionVariableName
  }

  get configurationCollectionVariableName () {
    return this.constructor.configurationCollectionVariableName
  }

  static get GqlTypeName () {
    return this.name
  }

  static async ensureBooted () {
    await Model.configuration.ensureLoaded()
    if (!Model.skipContext) {
      await Model.context.ensureLoaded()
    }
  }

  hasAncestor (model, visited = []) {
    return this.hasAncestorMatching(ancestor => ancestor === model, visited)
  }

  hasAncestorMatching (evaluatorFn, visited = []) {
    if (_.includes(visited, this)) {
      // Circular reference
      return false
    }
    if (evaluatorFn(this)) {
      return true
    }

    if (!this.parent) {
      return false
    }

    visited.push(this)

    return this.parent.hasAncestorMatching(evaluatorFn, visited)
  }

  reactiveObjects = []
  createReactive (data, skipReactive = false) {
    if (skipReactive) return markRaw(_.clone(data))

    let reactiveData
    if (!this.modelScope.active) {
      return _.clone(data)
    }

    this.modelScope.run(() => {
      reactiveData = reactive(_.clone(data))
    })

    // this.reactiveObjects.push(reactiveData)

    return reactiveData
  }

  stopWatchers = []
  createWatcher (source, callback, options, sourceExpression) {
    let stopWatch

    this.modelScope.run(() => {
      stopWatch = watch(
        () => {
          const result = source()
          return [result, this.watcherRecomputeId]
        },
        (newValue, oldValue) => {
          if (callback.length === 2) {
            callback(newValue, oldValue)
          } else {
            callback()
          }
        },
        options
      )
    })

    stopWatch.sourceExpression = sourceExpression || source.toString()

    this.stopWatchers.push(stopWatch)
    return stopWatch
  }

  handleDestroyedWatcher (watcher) {
    _.each(this.computedWatchers, (watcherInfo, watcherName) => {
      if (watcherInfo.watcher === watcher) {
        this.computedWatchers[watcherName]?.stopper()
        delete this.computedWatchers[watcherName]
      }
    })
  }

  computedWatchers = {}
  createComputed (name, computeFunction) {
    const watcherInfo = {}
    watcherInfo.scope = this.modelScope
    watcherInfo.model = this

    this.modelScope.run(() => {
      // getCurrentScope() should aways return the current modelScope
      // getCurrentInstance() will return the current vue instance, if exists on the call stack

      watcherInfo.stopper = watchEffect(async (onCleanup) => {
        const newValue = computeFunction.call(this)
        this.computedData[name] = newValue

        onCleanup(() => {
          // Watcher debugging, we can break here to check if a watcher is being stopped
          // If an async operation is happening, this is where we can stop it
          // In our case the compute function is synchronous, so we don't need to do anything here
        })
      }, {
        flush: 'sync'
      })

      // To get the current vue model, vue will do the following:
      // getCurrentScope()?.vm || getCurrentInstance()?.proxy
      // Even if you unset these prior to initiating the watcher, a reference to the vue model will be kept in the watcher
      // It seems to be an issue when accessing a reactive property though a watcher function in a model invoked by a vue
      // If a vue invokes a watcher (computed) to be setup and the watcher accesses a reactive property in a model, it prevents the watcher from being torndown
      // This is because the reactive property holds references to the vue model, which holds references to the watcher

      this.computedWatchers[name]?.stopper?.()
      this.computedWatchers[name] = watcherInfo
    })

    return watcherInfo.stopper
  }

  children = new Set()

  addChildren (children) {
    _.each(children, child => {
      if (!child.isModel) throw new Error('Attempted to add a child when it is not a model')

      if (child.parent && child.parent !== this) throw new Error('Attempted to add a child when it is already owned')

      if (this.children.has(child)) return

      child.setParent(this)

      this.children.add(child)
    })
  }

  destroy () {
    // console.log('Destroying ' + this.typeName + ' ' + (this.isCollection ? 'collection' : this.id))

    // if (this.typeName === 'Screen' && this.id === 5) {
    //   console.log('SCREEN 5')
    // }
    this.beforeDestroy && this.beforeDestroy()

    Model.allBaseQueries.delete(this)

    if (this.querySubscription) {
      this.querySubscription.unsubscribe()
      // Unblock any awaits as this will never return due to the unsubscribe
      this.handleQueryResolution()
      this.silent = true // In case there is a request coming
    }

    if (this.observationResponse) {
      this.observationResponse.unsubscribe()
    }

    // Destroy the model scope that contains all the model's effects (watchers,
    // computed, etc.)
    this.modelScope.stop()

    // console.log('Destroying watchers: ' + this.stopWatchers.length)
    _.each(this.stopWatchers, stopWatch => stopWatch())
    // console.log('Destroying computed: ' + this.computedWatchers.length)
    _.each(this.computedWatchers, watcherInfo => {
      // if (!watcherInfo.active) {
      //   console.log(this.typeName + '.' + watcherInfo.name + ' already stopped')
      // } else {
      //   console.log('Stopping ' + this.typeName + '.' + watcherInfo.name)
      // }
      watcherInfo.stopper()
      watcherInfo.stopper = null
      if (watcherInfo.model) {
        watcherInfo.model = null
      }
      // watcherInfo.active = false
    })

    this.destroyed = true

    Model.trackDestroyed && Model.trackDestroyed(this)

    _.each(this.data, (child, fieldName) => {
      if (!child || !child.isModel) {
        return
      }

      if (child.destroyed) {
        return
      }

      if (!this.isCollection) {
        const { isConfigurationField, field } = this.fieldType(fieldName)
        if (isConfigurationField) {
          // Configuration models are global, don't destroy them!
          return
        }

        if (field.reference && !child.hasAncestor(this)) {
          // We don't own this model, don't destroy it!
          return
        }
      }

      // if (fieldName === 'todaysShowings') {
      //   console.log('TODAYS SHOWINGS')
      // }

      if (this.hasAncestor(child)) {
        // Don't recursively delete, we could be referencing something higher up the tree so can't rely on checking .destroyed
        return
      }

      // console.log('destroying child: ' + fieldName)

      child.destroy()
    })
    this.children.forEach(child => {
      if (!child || !child.isModel) {
        return
      }

      if (child.destroyed) {
        return
      }

      if (!child.hasAncestor(this)) {
        // We don't own this model, don't destroy it!
        return
      }

      if (this.hasAncestor(child)) {
        // Don't recursively delete, we could be referencing something higher up the tree so can't rely on checking .destroyed
        return
      }

      // console.log('destroying explicit child: ' + child.typeName)

      child.destroy()

      this.children.delete(child)
    })

    // // Hacky way to go in and directly clean up all reference to this reactive properties
    // _.each([this, ..._.values(this.reactiveProperties)], props => {
    //   const dep = props?.__ob__?.dep

    //   if (!dep) return

    //   _.each(dep.subs, sub => {
    //     if (!sub) return

    //     sub.newDeps = _.without(sub.deps, dep)
    //     sub.newDepIds = new Set(sub.depIds)
    //     sub.newDepIds.delete(dep.id)
    //     sub.cleanupDeps()
    //   })
    // })

    // Reset computed watchers
    this.computedWatchers = {}
  }

  static get currentTime () {
    return timeContainer.currentTime
  }

  get currentTime () {
    return timeContainer.currentTime
  }

  static get currentTimeWithSeconds () {
    return timeContainer.currentTimeWithSeconds
  }

  get currentTimeWithSeconds () {
    return timeContainer.currentTimeWithSeconds
  }

  static get permissionCategories () {
    return PERMISSION_CATEGORIES
  }

  get permissionCategories () {
    return PERMISSION_CATEGORIES
  }

  static get permissions () {
    return PERMISSIONS_BY_CATEGORY
  }

  get permissions () {
    return PERMISSIONS_BY_CATEGORY
  }

  /**
   * Affects only admin app for now. Will eventually affect all employee accessible apps. An optional array of
   * PermissionCategory names that, when not empty, will be used to determine if a user has the required permissions
   * to access or act upon a model.
   *
   * The app will look to see of the user has the appropriate DefaultPermissionAction (`View`, `Manage`, `Delete`)
   * permission for *any* of the specified categories. For example, if the model sets `employeeRequiredPermissionCategories`
   * to `['Circuit', 'Site']` and the app is determining "View" access, then the app will require that the user
   * has either the 'Circuit::View' or 'Site::View'.
   * @type {import('src/models/access-control/constants').PermissionCategory[]}
   */
  static employeeRequiredPermissionCategories = []

  /**
   * The action name(s) checked for each permission category to determine whether to show pages/forms.
   * @type {string | string[]}
   */
  static viewAction = 'View'

  /** @returns {boolean} */
  static get isViewPermitted () {
    if (!_.isNil(Model.context) && !_.isEmpty(this.employeeRequiredPermissionCategories)) {
      const viewActions = _.isArray(this.viewAction) ? this.viewAction : [this.viewAction]
      return _.some(viewActions, action => {
        const possibleViewPermissions = _.map(this.employeeRequiredPermissionCategories, category => `${category}::${action}`)
        return Model.context.isAnyPermitted(possibleViewPermissions)
      })
    }
    return true
  }

  /** @returns {boolean} */
  get isViewPermitted () {
    return this.constructor.isViewPermitted
  }

  /**
   * The action name(s) checked for each permission category to determine whether to show Save buttons.
   * @type {string | string[]}
   */
  static manageAction = 'Manage'

  /** @returns {boolean} */
  static get isManagePermitted () {
    if (!_.isNil(Model.context) && !_.isEmpty(this.employeeRequiredPermissionCategories)) {
      const manageActions = _.isArray(this.manageAction) ? this.manageAction : [this.manageAction]
      return _.some(manageActions, action => {
        const possibleManagePermissions = _.map(this.employeeRequiredPermissionCategories, category => `${category}::${action}`)
        return Model.context.isAnyPermitted(possibleManagePermissions)
      })
    }
    return true
  }

  /** @returns {boolean} */
  get isManagePermitted () {
    return this.constructor.isManagePermitted
  }

  /**
   * The action name(s) checked for each permission category to determine whether to show Delete buttons.
   * @type {string | string[]}
   */
  static deleteAction = 'Delete'

  /** @returns {boolean} */
  static get isDeletePermitted () {
    if (!_.isNil(Model.context) && !_.isEmpty(this.employeeRequiredPermissionCategories)) {
      const deleteActions = _.isArray(this.deleteAction) ? this.deleteAction : [this.deleteAction]
      return _.some(deleteActions, action => {
        const possibleDeletePermissions = _.map(this.employeeRequiredPermissionCategories, category => `${category}::${action}`)
        return Model.context.isAnyPermitted(possibleDeletePermissions)
      })
    }
    return true
  }

  /** @returns {boolean} */
  get isDeletePermitted () {
    if (this.isDeleted) return false

    return this.constructor.isDeletePermitted
  }
}

export class GqlError extends Model {
  static name = 'GqlError'

  static define () {
    this.fields = {
      code: {
        type: String
      },
      description: {
        type: String
      },
      backtrace: {
        type: String
      }
    }
  }

  static noId = true

  static get GqlTypeName () {
    return 'Error'
  }

  toString () {
    return this.name ? this.name : (this.typeName + ':' + this.id)
  }
}
